import { variationPackDisplay } from '@/utils/fulfillmentLineDisplay';
import { outboundCarrierBlock } from '@/utils/fulfillmentOutboundGate';
import { PendingParcelNotice } from '@/pages/fulfillment/components/PendingParcelNotice';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCell } from '@/pages/fulfillment/components/CheckCell';
import { StockHistoryLink } from '@/pages/fulfillment/components/StockHistoryLink';
import { v4 as uuidv4 } from 'uuid';
import { AttemptKeys } from '@/utils/attemptKeys';
import axios from 'axios';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { FulfillmentAdminClient, INBOUND_STATUS, inboundStatusLabel } from '@/infrastructure/FulfillmentAdminClient';
import type {
  BulkInboundFailure,
  CancelInboundLineRequest,
  FulfillmentOrderDetail,
  FulfillmentOrderLine,
  InboundRequestSummary,
  InboundRequestDetail,
  InboundExpectedEditReason,
  OrderComment,
  PurchaseOrderCandidate,
  RegisterUnrecognizedScanRequest,
  ScanCodeMatch,
  ScanCodeResponse,
  ScanPoolParcel,
  ScanPoolParcelRow,
} from '@/infrastructure/FulfillmentAdminClient';
import { canCancelInbound, canInboundLine, isOutboundReady } from '@/utils/fulfillmentLineStatus';
import { FulfillmentSound } from '@/pages/fulfillment/components/outboundSound';
import { isCompleteInboundBulkResult } from '@/pages/fulfillment/components/bulkResult';
import { speak } from '@/pages/fulfillment/components/fulfillmentVoice';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { AUTO_DISMISS, useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import { usePromptDialog } from '@/pages/fulfillment/components/usePromptDialog';
import { describeFailure, serverRefusal } from '@/utils/fulfillmentErrors';
import { waybillOfParcelQr } from '@/utils/parcelQr';
import { parseCancelRequestHold } from '@/utils/cancelRequestHold';
import { toScanAscii } from '@/utils/scanAscii';
import { PrintDispatcher, PrintFailureNotice } from '@/pages/fulfillment/components/PrintDispatcher';
import { productCodeLabelJob, type ProductCodeLabelInput } from '@/pages/fulfillment/components/ProductCodeLabel';
import { internalBarcodeFor } from '@/utils/internalBarcode';
import { packingSheetJob, printParcels, sheetOutcomesOf } from '@/pages/fulfillment/components/parcelPaperwork';
import { ReturnLabelSheet } from '@/pages/fulfillment/components/ReturnLabelSheet';
import { readScanCode } from '@/pages/fulfillment/components/scanReading';
import type { PrintFailure, PrintJob, PrintOutcome, ReturnLabel } from '@/models/FulfillmentPrint';
import { OrderPdfPreview } from '@/pages/oms/orders/[id]/OrderPdfPreview';
import {
  DELEO_LABEL_PAGE_STYLE,
  currentOutboundShipmentOf,
  isPendingParcel,
  deleoLabelNumberOf,
  deleoLabelRefusalOf,
  deleoTrackingOf,
  isDeleoParcel,
  toDeleoLabelOrder,
} from '@/utils/fulfillmentDeleoLabel';
import { StockClient } from '@/infrastructure/StockClient';
import type { ProductLookupResponse } from '@/infrastructure/StockClient';
import { productAttributesClient, type BarcodeOwner } from '@/infrastructure/ProductAttributesClient';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { ROUTES, VIEW_ORDERS_ORDER_PARAM, VIEW_ORDERS_RETURN_PARAM, VIEW_ORDERS_SEARCH_PARAM } from '@/routes/routes';
import { fulfillmentSourceLabel } from '@/utils/fulfillmentSource';
import { countryTagOf } from '@/utils/countryFlag';
import { titleChipLabel, wireframeStatusClass } from '@/utils/fulfillmentOrderStatus';
import { activityRows, clockTime, logTimestamp, MISSING } from './activityLog';
import {
  EXISTING_INVENTORY,
  isV1WarehouseLocation,
  lineLocations,
  lineShownLocations,
  matchesSearchedTracking,
  orderLinesForDisplay,
  sourcingRouteLabel,
} from './orderLineView';
import { ExpectedInboundPanel } from './components/ExpectedInboundPanel';
import { OperatorComments } from './components/OperatorComments';
import { CommentStatusChip } from '@/pages/fulfillment/components/CommentStatus';
import { orderStatusOf, type OrderCommentStatus } from '@/models/OrderCommentStatus';
import { LineMemoThread } from './components/LineMemoThread';
import { ScannedLineComments } from './components/ScannedLineComments';
import { InboundCommentThread } from '@/pages/fulfillment/components/InboundCommentThread';
import { lineMemos, orderComments } from './commentNote';
import { readShelfCode, readsAsAName, SHELF_CODE_RULE } from './shelfCode';
import { arrivedLine, isCancelledOrder, oldestWaitingLine } from './runInbound';
import { LiveBarcodeFeed, ON_SCREEN } from './components/LiveBarcodeFeed';
import type { BarcodeScan } from './components/LiveBarcodeFeed';
import { feedDayOf, loadTodaysScans, saveTodaysScans } from './liveBarcodeFeedStore';
import { CancelInboundModal } from './components/CancelInboundModal';
import type { CancelInboundTarget } from './components/CancelInboundModal';
import { ReturnRestockModal } from './components/ReturnRestockModal';
import type { ReturnRestockLine } from './components/ReturnRestockModal';
import { ReturnLabelModal } from './components/ReturnLabelModal';
import { BarcodeCell } from '@/pages/fulfillment/components/BarcodeCell';
import { CancelledGoodsNote } from '@/pages/fulfillment/components/CancelledGoodsNote';
import { PackingRecordsLog } from '@/pages/fulfillment/components/PackingRecords';
import { ScanDeskSettingsModal } from './components/ScanDeskSettingsModal';
import {
  autoPrintedBefore,
  forgetAutoPrint,
  forgetLabelOwed,
  labelOwed,
  loadAutoPrintOnScan,
  rememberAutoPrint,
  rememberLabelOwed,
  saveAutoPrintOnScan,
} from './autoPrintSetting';
import { EditExpectedQtyModal } from './components/EditExpectedQtyModal';
import type { EditExpectedQtyTarget } from './components/EditExpectedQtyModal';
import { PurchaseOrderLookupModal } from './components/PurchaseOrderLookupModal';
import { SendToUnrecognizedModal } from './components/SendToUnrecognizedModal';
import { PoolParcelNotice } from './components/PoolParcelNotice';
import type { SendToUnrecognizedTarget } from './components/SendToUnrecognizedModal';
import { asciiScanField } from '../components/asciiScanField';
import { CameraScanField } from '../components/CameraScanField';
import { ShelfField } from '../components/ShelfField';
import { SCAN_SETTLED_MS, useScanInput } from '../components/useScanInput';
import { InboundReceivedQtyInput } from '@/pages/fulfillment/viewOrders/components/InboundReceivedQtyInput';
import { CopyIcon } from '@/components/ui/badge/CopyIcon';
import {
  CheckIcon,
  CommentIcon,
  GearIcon,
  PrintIcon,
  ScanIcon,
  SearchIcon,
  WarningIcon,
} from '@/pages/fulfillment/components/UltimateIcons';
import { ProductImageZoom } from '@/pages/fulfillment/components/ProductImageZoom';
import { orderSkus, readSkuStock, type SkuStock } from './lineStock';
import { AssignBarcodeDialog, type AssignBarcodeOption } from './AssignBarcodeDialog';
import '../wireframe.css';
import '../ultimateDesign.css';
import './viewOrders.css';

/** Where a scanned product barcode sits on the order currently on screen. */
interface ScannedProductLine {
  lineNo: number;
  arrived: boolean;
  /** An Existing Inventory line: the desk scans it to check the unit taken off the shelf, so a match is a confirmation, not a repeat (SSD-7990). */
  fromShelf?: boolean;
}

/**
 * The match for a product already in on one order. A shelf line outranks a bought one of the same product: the desk
 * scanning it is checking what it just picked, and it is marked so the scan confirms instead of warning (SSD-7990).
 */
const arrivedMatchFor = (
  lines: Array<{ lineNo: number; sku: string; lineStatus: string; sourcingRoute?: string | null }>,
  sku: string,
): ScannedProductLine | undefined => {
  const arrivedLines = lines.filter((line) => line.sku === sku && isOutboundReady(line.lineStatus));
  const arrived = arrivedLines.find((line) => line.sourcingRoute === EXISTING_INVENTORY) ?? arrivedLines[0];
  if (!arrived) {
    return undefined;
  }
  return {
    lineNo: arrived.lineNo,
    arrived: true,
    ...(arrived.sourcingRoute === EXISTING_INVENTORY && { fromShelf: true }),
  };
};

interface ScanBanner {
  kind: 'RETURN_WAYBILL' | 'INBOUND_REQUEST' | 'PRODUCT_BARCODE' | 'UNKNOWN';
  /** The code the desk read; the dead end needs it to register the parcel it came off. */
  code?: string;
  /** The pool entry the miss parked, so describing the parcel fills that row rather than adding a second. */
  parkedScanId?: string;
  /** Set on RETURN_WAYBILL: the customer-facing number read off the parcel that came back. */
  trackingNumber?: string;
  orderId?: number;
  docId?: string | undefined;
  inboundNo?: string | undefined;
  sku?: string | undefined;
  productName?: string | null | undefined;
  packSize?: number | undefined;
  match?: ScannedProductLine | undefined;
  /** A repeated scan of the same line re-reads its notes, without replaying any receiving action. */
  commentReadId?: number;
  /** Where the scan just put the goods; set only once the inbound came back. */
  receivedAt?: string;
  /** Set on a PRODUCT_BARCODE read against a shared waybill's run: that waybill, whose orders the scan searched [SSD-7413]. */
  runCode?: string;
}

/**
 * One of the things a code turned out to be. The same number can sit on a return waybill and on an
 * inbound request; picking the first silently sent a returned parcel into an arrival count [T07].
 */
interface ScanCandidate {
  kind: 'ORDER_WAYBILL' | 'RETURN_WAYBILL' | 'INBOUND_REQUEST' | 'PRODUCT_BARCODE';
  orderId?: number;
  trackingNumber?: string;
  docId?: string;
  inboundNo?: string;
  sku?: string;
  productName?: string | null;
}

/**
 * One shared waybill's parcels [15-1]. Every one stays offered until the desk scans something else or says
 * neither [SSD-7416]; `current` is the one on screen (none until the desk names it), `done` the ones finished.
 */
interface MatchedRun {
  code: string;
  candidates: ScanCandidate[];
  current: ScanCandidate | null;
  done: ScanCandidate[];
}

/** What one order card shows; the card the page's actions work on reads it from the screen state. */
interface OrderCardView {
  live: boolean;
  detail: FulfillmentOrderDetail;
  lines: FulfillmentOrderLine[];
  displayLines: FulfillmentOrderLine[];
  locations: ReturnType<typeof lineLocations>;
  logRows: ReturnType<typeof activityRows>;
  comments: OrderComment[];
  orderThread: OrderComment[];
  shelf: string;
  shelfDirty: boolean;
  shelfSaved: boolean;
  selected: number[];
  picked: Set<number>;
  orderCancelled: boolean;
  held: boolean;
  outbounded: boolean;
  shippable: boolean;
  pendingInbound: FulfillmentOrderLine[];
  lastPending: FulfillmentOrderLine | undefined;
  bulkNote: string;
}

/** The English and Korean names a product code sticker prints (SSD-7611). */
type LabelNames = Pick<ProductCodeLabelInput, 'name' | 'nameKr'>;

/** The desk's answer to "which product is this barcode for": a line, a close, a scan fired at it, or "not a product". */
type BarcodePickAnswer = number | null | { scanned: string } | 'not-product';

/**
 * An EAN-8, a 12-digit UPC-A (US products) or an EAN-13 with a valid check digit: the shape of a product barcode
 * (SSD-7677). Only such a code is offered to the product on screen. An unregistered waybill of the same length can
 * pass by chance, so the picker keeps a "Not a product barcode" way out.
 */
const looksLikeProductBarcode = (code: string): boolean => {
  if (!/^(\d{8}|\d{12}|\d{13})$/.test(code)) {
    return false;
  }
  const digits = code.split('').map(Number);
  const check = digits.pop() as number;
  const sum = digits.reverse().reduce((total, digit, at) => total + digit * (at % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
};

/** What the desk just opened or finished, so a run moves on only when it was the run's own parcel. */
type ProcessedParcel = { orderId: number } | { docId: string };

/** An order's waybill and its return open the same order, so either one is that order's parcel. */
const isParcel = (candidate: ScanCandidate, parcel: ProcessedParcel): boolean =>
  'docId' in parcel
    ? candidate.kind === 'INBOUND_REQUEST' && candidate.docId === parcel.docId
    : candidate.kind !== 'INBOUND_REQUEST' && candidate.orderId === parcel.orderId;

/**
 * A code the scanner fired, or an Expected Inbound row the desk clicked. A row opens a request just as a scan
 * does, so it waits its turn too: opened mid-scan, a product scan behind it would read the wrong screen.
 */
type QueuedWork =
  | string
  | { openInbound: { docId: string; inboundNo?: string } }
  | { reopenOrder: number; returnTracking?: string }
  | { reopenInbound: string };

/**
 * What is on screen, kept in the address so a reload, Back or Forward shows the same order or request
 * [SSD-7226 D4]. Only the record is read again; the search that found it is never run again, because a
 * search can receive a product, print a label or register an unknown parcel. The order param lives in
 * routes.ts because Order Detail links straight to it [SSD-7296].
 */
const VIEW_ORDERS_INBOUND_PARAM = 'inbound';
// The return param (a returned parcel's waybill beside the order) lives in routes.ts too: Arrival Sort links to it [SSD-7527].
const VIEW_PARAMS = [VIEW_ORDERS_ORDER_PARAM, VIEW_ORDERS_INBOUND_PARAM, VIEW_ORDERS_RETURN_PARAM];

/** What the address asks the screen to show: an order, an inbound request, or nothing. A linked search outranks both. */
type AddressedView = { order: number } | { inbound: string } | null;
const viewInAddress = (params: URLSearchParams): AddressedView => {
  if (params.get(VIEW_ORDERS_SEARCH_PARAM)) {
    return null;
  }
  const orderParam = Number(params.get(VIEW_ORDERS_ORDER_PARAM));
  if (Number.isSafeInteger(orderParam) && orderParam > 0) {
    return { order: orderParam };
  }
  const inboundParam = params.get(VIEW_ORDERS_INBOUND_PARAM);
  return inboundParam ? { inbound: inboundParam } : null;
};

/** [BR-55] an unqualified success over a part-refused batch is how stock silently goes missing. */
const bulkInboundToast = (
  inbounded: number,
  asked: number,
  refused: number,
  passedOver: number,
  unconfirmed: number,
): Toast => {
  if (refused === 0 && passedOver === 0 && unconfirmed === 0) {
    return { text: `\u2713 Inbound complete — ${inbounded} line(s)` };
  }
  const left = [
    refused > 0 ? `${refused} refused` : null,
    passedOver > 0 ? `${passedOver} passed over` : null,
    // A malformed or incomplete answer proves nothing about its lines, so they are neither in nor refused.
    unconfirmed > 0 ? `${unconfirmed} unconfirmed` : null,
  ]
    .filter((clause): clause is string => clause !== null)
    .join(' · ');
  return {
    text: `${inbounded} of ${asked} line(s) inbounded — ${left}`,
    failed: unconfirmed > 0 ? 'partial' : inbounded === 0 && refused > 0 ? true : 'partial',
  };
};

const RESTOCK_NOTE = 'Current Stocks quantities and locations are updated';
/** The line table's column count, so a line's memo row can span it (SSD-7265). */
const LINE_COLUMNS = 14;

/** [BR-55] holds for a return restock too: a refused line never reads as restocked, and none landing is an error. */
const returnRestockToast = (restocked: number, asked: number): Toast => {
  if (restocked === asked) {
    return { text: `\u2713 Restocked — ${asked} item(s)`, note: RESTOCK_NOTE };
  }
  return {
    text: `${restocked} of ${asked} item(s) restocked — ${asked - restocked} refused`,
    failed: restocked === 0 ? true : 'partial',
    ...(restocked > 0 && { note: RESTOCK_NOTE }),
  };
};

/**
 * How long a Deleo-family sticker print may go without react-to-print reporting back. Chrome holds the page while
 * the dialog is open, so this only runs out when the print never started.
 */
const DELEO_PRINT_TIMEOUT_MS = 60_000;

/** How long a Deleo-family print waits for a scanned order's sticker to be drawn: 20 x 25ms. */
const DELEO_DRAW_WAITS = 20;
const DELEO_DRAW_WAIT_MS = 25;

/** The order's carrier label ready to print — a drawn Deleo-family sticker or archived pdf — or why there is none. */
type CarrierLabel = { job: PrintJob } | { pdf: Blob; trackingNumber: string } | { missing: string };

/** What became of the carrier label in one order's print: out, refused by the printer, or not issued yet. */
type CarrierLabelPrint = 'out' | 'refused' | 'missing';

/** The shape this screen has always described an outcome in; translated at one point below [T03]. */
interface Toast {
  text: string;
  note?: string;
  /** `true` when the outcome failed outright; `'partial'` when some of what was asked happened and some did not. */
  failed?: boolean | 'partial';
}

/**
 * An outbound-side write in flight. It waits on the shop's status push for seconds, and disabled buttons
 * over a status that has not moved yet read as a write that never happened [SSD-7390].
 */
interface OutboundWork {
  orderId: number;
  kind: 'outbound' | 'inbound-outbound' | 'cancel';
}

const outboundWorkLabel = ({ orderId, kind }: OutboundWork): string =>
  kind === 'cancel'
    ? `Cancelling outbound for order ${orderId} — please wait`
    : kind === 'inbound-outbound'
      ? `Completing inbound + outbound for order ${orderId} — please wait`
      : `Completing outbound for order ${orderId} — please wait`;

const hasLeft = (detail: FulfillmentOrderDetail): boolean =>
  detail.header.fulfillmentStatus === 'OUTBOUNDED' || detail.header.fulfillmentStatus === 'SHIPPED';

/**
 * Complete only once the order read back says it left: a 200 over an order still in the warehouse is not a
 * finish, and saying so sends the desk to look rather than press Outbound again [SSD-7390].
 */
const outboundToast = (detail: FulfillmentOrderDetail, headline: string): Toast => {
  const { orderId, fulfillmentStatus, wcStatus } = detail.header;
  return hasLeft(detail)
    ? {
        text: `✓ ${headline} complete — Order ${orderId}`,
        note: `Store status: ${wcStatus ?? 'unknown'} · ready for the next scan`,
      }
    : {
        text: `${headline} sent — Order ${orderId} still reads ${fulfillmentStatus}`,
        note: 'Check the order before pressing Outbound again',
        failed: 'partial',
      };
};

/** The lines the last batch refused, and the work they missed, so the banner names what did not happen. */
interface RefusedLines {
  work: 'inbounded' | 'restocked';
  lines: BulkInboundFailure[];
}

const NO_REFUSALS: RefusedLines = { work: 'inbounded', lines: [] };
/**
 * Where a JIT line is booked while the order has no Shelf. The server books every arrival onto a location
 * and the desk scans the slot only after the click, so the receipt goes through the packing desk instead of
 * waiting for the slot; a line that ships on the same click is consumed there at once [SSD-7226 D3].
 */
export const PACKING_LOCATION = 'PACKING';
/** On screen and aloud when the desk leaves an order it received without a Shelf. */
const SCAN_THE_SHELF = 'Scan the shelf';
/** The button on that card: it reopens the order the desk left, ready for the slot scan. */
const GO_BACK = 'Go back';

interface AdminActionErrorBody {
  message?: string | string[];
  violations?: string[];
  /** Set when the refusal is one the screen can answer rather than only report. */
  code?: string;
}

/** The parcel the catalog could not place; parking it is exactly what the pool is for [T11]. */
const UNKNOWN_BARCODE = 'UNKNOWN_BARCODE';
const UNPLACEABLE_FALLBACK = 'No product carries this code.';

/** What the desk does with the parcel in hand after a refused send; the server's sentence says only why. */
const UNPLACEABLE_GUIDANCE =
  'Look for the product barcode on the item itself (not the shipping label or the outer box) and scan that instead. ' +
  'If the item really carries no listed barcode, press Park it anyway and keep the parcel aside until it is matched on the Unrecognized Tracking List.';
const POOL_ENTRY_CLOSED_GUIDANCE =
  'Its pool entry was already matched or removed. Check the Unrecognized Tracking List before sending it again, and keep the parcel aside until then.';
const POOL_SEND_FAILED_GUIDANCE =
  'Nothing was parked. Keep the parcel at the desk and send it again; if it is refused again, set it aside and tell the floor lead.';

/** A registration the server refused, held on screen beside the dead end until the desk answers it. */
interface PoolRefusal {
  request: RegisterUnrecognizedScanRequest;
  reason: string;
  /** What to do with the parcel now, in floor terms. */
  guidance: string;
  /** Only an unplaceable barcode may be parked anyway; any other refusal is not the catalog's to waive. */
  parkable: boolean;
}

/**
 * The server's reason without its wire advice: "register it again with force" names a request flag, and
 * the desk is offered that as the Park it anyway button instead.
 */
const plainRefusalOf = (said: string | undefined, fallback: string): string => {
  const reason = said?.split(';')[0]?.trim();
  if (!reason) {
    return fallback;
  }
  return reason.endsWith('.') ? reason : `${reason}.`;
};

const GENERIC_ERROR = 'The request failed. Please try again shortly.';

/**
 * Coupang's small QR hands the scanner `[V1]<barcode>`. The number after the prefix is the barcode
 * the catalog knows, so the prefix is stripped before anything is asked of the wire.
 */
const COUPANG_QR_PREFIX = /^\[V1\]/i;

/** The table the counts are typed into; a cursor inside it is working the lines and is not pulled back to the scan box. */
const INBOUND_LINES_TABLE = 'table[aria-label="Inbound request lines"]';

/** Every line counted to exactly what was expected, and every line given a shelf — what a FULL confirm needs. */
const linesWhole = (lines: InboundRequestDetail['lines'], locations: Record<string, string>): boolean =>
  lines.length > 0 &&
  lines.every((line) => line.qtyReceived === line.qtyExpected && (locations[line.sku] ?? '').trim() !== '');

/** The shelf a line shows read-only: its own assignment, or wherever the sku already sits. Empty means the desk types it. */
const shownShelf = (line: InboundRequestDetail['lines'][number]): string =>
  line.locationCode ?? line.suggestedLocationCode ?? '';

/**
 * A scan typed while the OS keyboard was in Korean mode is read as the ASCII the scanner meant [SSD-7050], and a
 * parcel QR as the waybill its 1D barcode holds, so both codes on one parcel open the same thing [SSD-7393].
 */
const scannedCode = (raw: string): string => waybillOfParcelQr(toScanAscii(raw).trim().replace(COUPANG_QR_PREFIX, ''));

/** The route the goods took to the dock; OTHER carries the channel the operator typed, and is useless without it. */
const describeInboundRoute = (doc: InboundRequestSummary): string =>
  doc.source === 'ETC' || doc.source === 'OTHER'
    ? doc.otherChannel
      ? `OTHER (${doc.otherChannel})`
      : 'OTHER'
    : fulfillmentSourceLabel(doc.source);

/** Controls the desk is deliberately using keep the cursor; a click anywhere else hands it back to the scan box. */

const CURSOR_OWNERS =
  'input, textarea, select, button, a, label, summary, dialog, [role="dialog"], [role="button"], [contenteditable="true"]';

const failureOf = (err: unknown) => (axios.isAxiosError<AdminActionErrorBody>(err) ? err : undefined);

/**
 * The count's refusal of a code the open request never asked for, which is the one refusal the desk
 * answers itself [SSD-7204]. Any other failure of the count keeps the server's own words.
 */
const notOnRequest = (err: unknown): boolean =>
  failureOf(err)?.response?.status === 409 && /did not ask for/i.test(serverRefusal(err) ?? '');

/** The store's own eight statuses, chipped the way the wireframe colours them. */
const STORE_STATUS_CLASS: Record<string, string> = {
  processing: 'st-processing',
  completed: 'st-completed',
  shipped: 'st-completed',
  'prepare-shipment': 'st-prepare',
  'prepare shipment': 'st-prepare',
  'on-hold': 'st-hold',
  pending: 'st-hold',
  refunded: 'st-refunded',
  failed: 'st-refunded',
};

/** Nearly half of dev carries no store status; colouring the gap green would invent one. */
const storeStatusClass = (status: string | null): string =>
  STORE_STATUS_CLASS[(status ?? '').toLowerCase()] ?? 'st-unknown';

/** Said when the PACKING invoice could not be read for a print: the carrier label still goes, the sheet does not. */
const PACKING_UNREAD = 'The packing invoice could not be read — PACKING not printed; print it from Packing Invoice.';

/** The store's own word for an order the customer or the shop called off. */
const CANCELLED_STORE_STATUS = 'cancelled';
const LEFT_THE_WAREHOUSE = ['OUTBOUNDED', 'SHIPPED', 'CANCELLED'];
/**
 * An order with a parcel in hand and nowhere to put it: a line that arrived (not one booked from stock),
 * an order that has not gone out, and no Shelf. Read off the order itself, so it holds however the
 * parcel came in and until the slot is saved or the order leaves [SSD-7226 D3].
 */
const owesShelf = (detail: FulfillmentOrderDetail | null): detail is FulfillmentOrderDetail =>
  detail !== null &&
  !(detail.header.temporaryLocation ?? '').trim() &&
  !LEFT_THE_WAREHOUSE.includes(detail.header.fulfillmentStatus) &&
  (detail.header.wcStatus ?? '').toLowerCase() !== CANCELLED_STORE_STATUS &&
  detail.lines.some((line) => line.sourcingRoute !== 'EXISTING_INVENTORY' && isOutboundReady(line.lineStatus));

/** An order that has gone out or been called off; nothing on this screen can finish it any more [SSD-7394]. */
const isClosedOrder = (detail: FulfillmentOrderDetail): boolean =>
  LEFT_THE_WAREHOUSE.includes(detail.header.fulfillmentStatus) ||
  (detail.header.wcStatus ?? '').toLowerCase() === CANCELLED_STORE_STATUS;

/** A line past the desk reads as done — the arrival chip is not asking whether it can still ship. */
const LINE_DONE_STATUSES = ['INBOUNDED', 'READY', 'OUTBOUNDED', 'SHIPPED'];

const isLineDone = (lineStatus: string): boolean => LINE_DONE_STATUSES.includes(lineStatus);

/** A received or cancelled request is done with: it opens to be read, never to be counted again. */
const isSettledInbound = (status: string | undefined): boolean =>
  status === INBOUND_STATUS.RECEIVED || status === INBOUND_STATUS.CANCELLED;

/** Cancelled is the one state nothing can be typed into; a received request is corrected like an open one [SSD-7266]. */
const isCancelledInbound = (status: string | undefined): boolean => status === INBOUND_STATUS.CANCELLED;

/** The statuses still waiting at the dock; asked for one at a time so the newest received requests cannot crowd them out. */
const EXPECTED_INBOUND_STATUSES = [INBOUND_STATUS.REQUESTED, INBOUND_STATUS.PARTIAL];
/** A ceiling on the walk, so a runaway cursor cannot hold the scan hub's first paint hostage. */
const EXPECTED_INBOUND_MAX_PAGES = 10;

/** Every waybill the request carries; a split shipment registers one per box, and any of them is this request. */
const registeredTrackings = (doc: InboundRequestSummary): string[] => [
  ...(doc.tracking ? [doc.tracking] : []),
  ...(doc.trackings ?? []),
];

/** Lines grouped by where they go, in line order, so each location is booked with the lines it holds. */
const groupByLocation = (planned: Array<{ lineNo: number; locationCode: string }>): Map<string, number[]> => {
  const groups = new Map<string, number[]>();
  for (const { lineNo, locationCode } of [...planned].sort((a, b) => a.lineNo - b.lineNo)) {
    groups.set(locationCode, [...(groups.get(locationCode) ?? []), lineNo]);
  }
  return groups;
};

export default function FulfillmentViewOrdersPage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  // Barcode, size and image live on the product, never on the order line — the live OMS desk looks
  // them up the same way, and the barcode typed here is saved back to the product master.
  const stock = useMemo(() => new StockClient(), []);
  // The desk works a long order from the top down, so a cursor coming back must not scroll the page away
  // from the line it was on [SSD-7136]; the one scroll kept is the missing-location prompt in planLocations.
  const searchInput = useScanInput({ preventScroll: true });
  /**
   * The code last sent from the scan box, and whether the desk typed since. The code stays in the box
   * once sent [SSD-7135], so a scanner that follows its Enter with a second line terminator submits it
   * again: a repeat of the untouched code within a scan's settling time is that, not a second parcel.
   */
  const lastSubmit = useRef<{ code: string; at: number } | null>(null);
  const typedSinceSubmit = useRef(false);
  /** Held across a retry so a double-post is one comment, the way every other 2.0 screen posts. */
  // The key of a note that has not landed yet, with what it said, so only a retry of the same note replays it (SSD-7609).
  const pendingCommentRef = useRef<{ key: string; said: string } | null>(null);
  // One key per line, kept across a partial-failure retry: a fresh key on a line that already went back is refused.
  const putawayKeysRef = useRef(new AttemptKeys());

  // Declared before the screen state so a reload can start from what the address names [SSD-7226 D4].
  const [searchParams, setSearchParams] = useSearchParams();
  const { key: locationKey } = useLocation();
  /**
   * The order or request the address names that is not on screen yet. From the first paint the search box
   * carries the number and the empty-desk panel stays out of the way, so a reload does not flash "Waiting
   * for scan" before the order comes back; cleared once the reopen lands or fails.
   */
  const [pendingView, setPendingView] = useState<AddressedView>(() => viewInAddress(searchParams));
  const [code, setCode] = useState(() => {
    const view = viewInAddress(searchParams);
    return view && 'order' in view ? String(view.order) : '';
  });
  /**
   * A code the server matched more than once; the desk chooses, because only it holds the parcel [T07]. The
   * choice stays on screen after a pick, so none of the parcels the waybill named is missed [15-1].
   */
  const [matchedRun, setMatchedRun] = useState<MatchedRun | null>(null);
  // Read when an action finishes rather than when it started, so a run dismissed meanwhile is not resumed.
  const matchedRunRef = useRef<MatchedRun | null>(null);
  matchedRunRef.current = matchedRun;
  // Held in the ref at once too, so a step later in the same scan reads the run this one left.
  const commitMatchedRun = (next: MatchedRun | null) => {
    matchedRunRef.current = next;
    setMatchedRun(next);
  };
  const choosing = matchedRun != null && matchedRun.current == null;
  const { toasts, showSuccess, showError, showWarning, showFailure, dismiss: dismissToast } = useFulfillmentToast();
  const [poolRefusal, setPoolRefusal] = useState<PoolRefusal | null>(null);
  const [order, setOrder] = useState<FulfillmentOrderDetail | null>(null);
  /**
   * Each line's on-hand and available stock, read for this order's skus only and again whenever the order is
   * re-read, so an inbound shows up in the figures (SSD-7521). Supplementary: a failed read shows nothing.
   */
  const [skuStock, setSkuStock] = useState<Record<string, SkuStock>>({});
  useEffect(() => {
    const skus = orderSkus(order?.lines ?? []);
    if (skus.length === 0) {
      setSkuStock({});
      return;
    }
    let current = true;
    readSkuStock(client, skus)
      .then((stock) => {
        if (current) {
          setSkuStock(stock);
        }
      })
      .catch(() => {
        if (current) {
          setSkuStock({});
        }
      });
    return () => {
      current = false;
    };
  }, [client, order]);
  /** The code that opened the order on screen, so the line carrying that waybill is pulled up and marked. */
  const [searchedTracking, setSearchedTracking] = useState<string | null>(null);
  const [banner, setBanner] = useState<ScanBanner | null>(null);
  const commentReadSequence = useRef(0);
  const [sendToPool, setSendToPool] = useState<SendToUnrecognizedTarget | null>(null);
  /** The box just scanned is still parked in the unrecognized pool under the order it opened (SSD-7442). */
  const [parkedHere, setParkedHere] = useState<{ orderId: number; parcel: ScanPoolParcel } | null>(null);
  /** The dead-end code M2 is open for; null when the desk has not asked for the order-number route. */
  const [lookupCode, setLookupCode] = useState<string | null>(null);
  /** The inbound line whose expectation M6 is rewriting; null when the modal is closed. */
  const [expectedEdit, setExpectedEdit] = useState<EditExpectedQtyTarget | null>(null);
  /**
   * Actions in flight, counted: two typed counts can be saving at once, and the desk is busy until the
   * last of them lands — a boolean would have freed the confirm when the first one did.
   */
  const [actionsInFlight, setActionsInFlight] = useState(0);
  const beginAction = () => setActionsInFlight((count) => count + 1);
  const endAction = () => setActionsInFlight((count) => Math.max(0, count - 1));
  /** The confirm (by hand or by itself) is running: the lines are read-only until it is over. */
  const [confirming, setConfirming] = useState(false);
  /**
   * A Shelf save in flight, kept apart from the action's own busy: the slot is scanned into the Shelf while
   * an inbound is still saving [SSD-7226], and whichever of the two finishes first must not free the buttons
   * and the scan queue while the other is still running.
   */
  const [shelfSaving, setShelfSaving] = useState(false);
  const busy = actionsInFlight > 0 || shelfSaving;
  /**
   * A print in flight holds only the Print button. It used to hold `busy`, so Inbound, Outbound and the scan
   * queue sat disabled through the label read, the sheet drawing and the print dialog, seconds per parcel.
   */
  const [printing, setPrinting] = useState(false);
  /** Prints run one after another: Deleo-family stickers and print frames are one at a time on this page. */
  const printChain = useRef<Promise<void>>(Promise.resolve());
  const printsOut = useRef(0);
  /** The order a print is in flight for; its Deleo-family sticker stays drawn while the screen moves on. */
  const [printingOrder, setPrintingOrder] = useState<FulfillmentOrderDetail | null>(null);
  /** Named on screen until the write and the order read after it are both back [SSD-7390]. */
  const [outboundWork, setOutboundWork] = useState<OutboundWork | null>(null);
  /** Held in a ref too, so a second press landing before the first one's render is refused, not sent twice. */
  const outboundWorkRef = useRef<OutboundWork | null>(null);
  const { ask, askConfirm, dialog: confirmDialog } = usePromptDialog();
  /** The open "which product is this barcode for" question, answered by the desk's pick or its cancel (SSD-7677). */
  const [assignAsk, setAssignAsk] = useState<{
    code: string;
    options: AssignBarcodeOption[];
    initial?: number | undefined;
    settle: (answer: BarcodePickAnswer) => void;
  } | null>(null);
  /** Codes the scanner fired while the desk was still working; a refused scan is a parcel nobody opened [8b]. */
  const [scanQueue, setScanQueue] = useState<QueuedWork[]>([]);
  const [scanning, setScanning] = useState(false);
  const [violations, setViolations] = useState<string[]>([]);
  const [failures, setFailures] = useState<RefusedLines>(NO_REFUSALS);
  /** Lines the run passed over rather than refused; already in, so they need reading, not fixing [D-q]. */
  const [skippedLines, setSkippedLines] = useState<BulkInboundFailure[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [shelf, setShelf] = useState('');
  const shelfRef = useRef<HTMLInputElement>(null);
  const [shelfDirty, setShelfDirty] = useState(false);
  const [shelfSaved, setShelfSaved] = useState(false);
  /**
   * The order whose Shelf the desk has typed into. Until another order loads, a reload of this one keeps the
   * field as the desk left it: an inbound's reload coming back during a slot scan used to put the old slot
   * back over the one just typed, and a reload read before the save landed would do the same [SSD-7226].
   */
  const shelfEdit = useRef<number | null>(null);
  /** Counts each edit of the Shelf, so a save knows whether the box still holds what it sent [SSD-7817]. */
  const shelfTyping = useRef(0);
  /** The order on screen as last read, so leaving it can be judged against what it actually holds. */
  const shownOrder = useRef<FulfillmentOrderDetail | null>(null);
  /** Set when a receipt left the order without a slot, so a finishing scan leaves the cursor in the Shelf. */
  const shelfAsked = useRef(false);
  /** The order "Go back" is reopening; when it loads, the cursor goes to its Shelf for the slot scan. */
  const returnToShelf = useRef<number | null>(null);
  const [shelfFocusFor, setShelfFocusFor] = useState<number | null>(null);
  /** Reopens a left order the way a typed order number does; set each render so it sees the screen as it is. */
  const goBackTo = useRef<(orderId: number) => void>(() => undefined);
  /** Each "Scan the shelf" card by the order it names, closed once that order's slot is saved. */
  const shelfAskToasts = useRef(new Map<number, number>());
  /**
   * Called as the screen leaves an order: one that still owes a slot is named on screen and aloud. Closing
   * the card only closes the card; the order asks again the next time it is left without a slot.
   */
  const askOnLeaving = useCallback(
    (leaving: FulfillmentOrderDetail | null) => {
      if (!owesShelf(leaving)) {
        return;
      }
      const orderId = leaving.header.orderId;
      const standing = shelfAskToasts.current.get(orderId);
      if (standing !== undefined) {
        dismissToast(standing);
      }
      // The desk hears it (sound and voice) and the order asks again the next time it is left without a slot,
      // so the card leaves after 6 s instead of waiting for a click on every scan (SSD-7705, FC request).
      const toastId = showWarning(
        SCAN_THE_SHELF,
        [`Order ${orderId} was received without a Shelf — go back and scan its slot`],
        { ...AUTO_DISMISS, action: { label: GO_BACK, run: () => goBackTo.current(orderId) } },
      );
      shelfAskToasts.current.set(orderId, toastId);
      FulfillmentSound.warning();
      speak(SCAN_THE_SHELF);
    },
    [dismissToast, showWarning],
  );
  /**
   * An order that no longer owes a slot closes the card that asked for one: outbounded straight to the packing
   * desk it never goes onto a shelf, so the desk is not sent back for a slot it does not need (SSD-7389).
   */
  const closeShelfAsk = useCallback(
    (detail: FulfillmentOrderDetail) => {
      const orderId = detail.header.orderId;
      const standing = shelfAskToasts.current.get(orderId);
      if (standing === undefined || owesShelf(detail)) {
        return;
      }
      dismissToast(standing);
      shelfAskToasts.current.delete(orderId);
    },
    [dismissToast],
  );
  const [comments, setComments] = useState<OrderComment[]>([]);
  /** The pane shows the notes about the order; a note about one line sits under that line (SSD-7265). */
  const orderThread = useMemo(() => orderComments(comments), [comments]);
  const threadStatus = useMemo(() => orderStatusOf(orderThread), [orderThread]);
  const [commentsOpen, setCommentsOpen] = useState(true);
  const [expected, setExpected] = useState<InboundRequestSummary[]>([]);
  // Today's feed survives a reload and starts empty the next day; it lives only in this browser [15-5].
  const [feed, setFeed] = useState<{ day: string; scans: BarcodeScan[] }>(() => {
    const now = new Date();
    return { day: feedDayOf(now), scans: loadTodaysScans(now) };
  });
  const [cancelTarget, setCancelTarget] = useState<CancelInboundTarget | null>(null);
  const [returnRestockOpen, setReturnRestockOpen] = useState(false);
  const [returnLabelOpen, setReturnLabelOpen] = useState(false);
  const [autoPrintOnScan, setAutoPrintOnScan] = useState(loadAutoPrintOnScan);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [catalog, setCatalog] = useState<Record<string, ProductLookupResponse>>({});
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;
  /**
   * Korean brand names by the English brand the line carries, from the sourcing mapper's table (SSD-7389). A
   * brand asked once is not asked again on this screen; one the table does not know keeps its English name.
   */
  const [brandKr, setBrandKr] = useState<Record<string, string>>({});
  const brandKrAsked = useRef(new Set<string>());
  const [inbound, setInbound] = useState<InboundRequestDetail | null>(null);
  /** The last product the open request counted, kept on the banner after its toast is gone [SSD-7204]. */
  const [lastCount, setLastCount] = useState<{ sku: string; at: string } | null>(null);
  const [inboundLocations, setInboundLocations] = useState<Record<string, string>>({});
  /** The request the typed locations belong to; another request's lines start from their own suggestions. */
  const inboundLocationsDocId = useRef<string | null>(null);
  /** Bumped by every read and every leave, so only the newest read of the newest request lands on screen. */
  const inboundReadSeq = useRef(0);
  /**
   * The confirm a whole request runs on its own, due as soon as the save that made it whole has landed; null
   * while none is due. There is no grace: the moment every line matches, the goods go in [SSD-7266].
   */
  const [autoConfirm, setAutoConfirm] = useState<{ docId: string } | null>(null);
  /**
   * Bumped at each moment the request may have just become whole: a count or an expectation the server
   * accepted, a shelf typed and left, a product scanned. Only those moments confirm on their own — a request
   * that opens already whole, or was left whole by someone else, waits for a hand on the button instead.
   */
  const [wholeCheck, setWholeCheck] = useState(0);
  const askWholeCheck = useCallback(() => setWholeCheck((count) => count + 1), []);
  const [inboundDone, setInboundDone] = useState<string | null>(null);
  const [lineQtyErrors, setLineQtyErrors] = useState<Set<number>>(new Set());
  /**
   * Something reached the server but the request could not be read back: a count, or the confirm itself. The
   * totals on screen are then older than the server's, so nothing is typed against them or confirmed from them.
   */
  const [inboundRefreshStale, setInboundRefreshStale] = useState<'count' | 'confirm' | null>(null);

  useEffect(() => {
    setLineQtyErrors(new Set());
  }, [inbound?.doc.docId]);

  const handleLineQtyErrorChange = useCallback((lineNo: number, hasError: boolean) => {
    setLineQtyErrors((previous) => {
      const next = new Set(previous);
      if (hasError) {
        next.add(lineNo);
      } else {
        next.delete(lineNo);
      }
      return next;
    });
  }, []);

  const scannedSku = banner?.kind === 'PRODUCT_BARCODE' ? banner.sku : undefined;
  const header = order?.header;
  /** A cancelled order is out of the inbound flow, whatever its lines still read, so nothing on it is received [SSD-7049]. */
  const orderCancelled = header ? isCancelledOrder(header) : false;
  const lines = useMemo(() => order?.lines ?? [], [order]);
  const locations = useMemo(() => lineLocations(order?.reservations ?? []), [order]);
  const shownLocations = useMemo(() => lineShownLocations(order?.reservations ?? []), [order]);
  const pendingInbound = useMemo(
    () => (orderCancelled ? [] : lines.filter((line) => canInboundLine(line.lineStatus))),
    [lines, orderCancelled],
  );
  const lastPending = pendingInbound.length === 1 ? pendingInbound[0] : undefined;
  const picked = useMemo(() => new Set(selected), [selected]);
  const shippable = useMemo(
    () => lines.some((line) => isLineDone(line.lineStatus)) && lines.every((line) => !canInboundLine(line.lineStatus)),
    [lines],
  );
  const logRows = useMemo(() => activityRows(order?.events ?? []), [order]);
  const held = Boolean(header?.holdReason);
  const carrierOutboundBlock = header
    ? outboundCarrierBlock(header.orderId, order?.tracking, order?.pendingParcels)
    : '';
  const outbounded = header?.fulfillmentStatus === 'OUTBOUNDED' || header?.fulfillmentStatus === 'SHIPPED';
  // CS's cancel request is finished on Order Detail's Confirm cancel, which asks FC each line's Keep or Return; undoing
  // the shipment here would skip that (SSD-7676).
  const cancelOutboundBlock =
    outbounded && parseCancelRequestHold(header?.holdReason) !== null
      ? 'A cancel request is filed: FC unpacks the parcel and presses Confirm cancel on Order Detail'
      : '';
  /** State 4: the desk scanned the customer's own waybill, so this parcel came back [BR-12] [L-S4-6]. */
  const returnMode = banner?.kind === 'RETURN_WAYBILL';
  /**
   * An order that has left the desk keeps its lines grey and untouchable, head included, so nothing on it is ticked,
   * typed or pressed by mistake; the grey says it, so no note repeats it. Cancel Outbound in the order bar is the way
   * back, and a customer return goes back on the shelf from Order Detail's Return putaway (SSD-7389).
   */
  const linesLocked = outbounded && !returnMode;
  const displayLines = useMemo(
    () => orderLinesForDisplay(lines, scannedSku, searchedTracking),
    [lines, scannedSku, searchedTracking],
  );
  const inboundSettled = isSettledInbound(inbound?.doc.status);
  const inboundCancelled = isCancelledInbound(inbound?.doc.status);
  const inboundReceived = inbound?.doc.status === INBOUND_STATUS.RECEIVED;
  /**
   * Whole: every line counted to exactly what was expected, every line with a shelf, nothing refused and
   * nothing unread. The server confirms FULL on nothing less, so nothing less is confirmed on its own.
   */
  const inboundWhole = useMemo(
    () =>
      inbound != null &&
      !inboundSettled &&
      !inboundRefreshStale &&
      lineQtyErrors.size === 0 &&
      linesWhole(inbound.lines, inboundLocations),
    [inbound, inboundSettled, inboundRefreshStale, lineQtyErrors, inboundLocations],
  );
  useEffect(() => {
    searchInput.ref.current?.focus({ preventScroll: true });
  }, [searchInput]);

  useEffect(() => {
    // A locked table carries no selection, so no bulk action can reach a line through it.
    if (linesLocked) {
      setSelected([]);
    }
  }, [linesLocked]);

  useEffect(() => {
    // Written only under the day it belongs to, so yesterday's feed is never saved as today's.
    const now = new Date();
    if (feed.day === feedDayOf(now)) {
      saveTodaysScans(feed.scans, now);
    }
  }, [feed]);

  useEffect(() => {
    // A desk left open overnight starts the new day with an empty feed rather than yesterday's scans.
    const now = new Date();
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timer = window.setTimeout(() => {
      const turned = new Date();
      setFeed({ day: feedDayOf(turned), scans: loadTodaysScans(turned) });
    }, nextMidnight.getTime() - now.getTime());
    return () => window.clearTimeout(timer);
  }, [feed.day]);

  // A retry key belongs to one order's post; carrying it across orders would fold two comments into one.
  useEffect(() => {
    pendingCommentRef.current = null;
  }, [header?.orderId]);

  useEffect(() => {
    // The scan hub opens on what is still on its way, so the desk sees it without leaving the screen.
    // Each waiting status is walked to its end: dev already holds more requested than one page.
    const waitingWith = async (status: string): Promise<InboundRequestSummary[]> => {
      const items: InboundRequestSummary[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < EXPECTED_INBOUND_MAX_PAGES; page += 1) {
        const response = await client.listInboundRequests({ status, limit: 100, ...(cursor && { cursor }) });
        items.push(...response.items);
        if (!response.nextCursor) {
          break;
        }
        cursor = response.nextCursor;
      }
      return items;
    };
    void Promise.all(EXPECTED_INBOUND_STATUSES.map(waitingWith))
      .then((pages) => setExpected(pages.flat()))
      .catch(() => setExpected([]));
  }, [client]);

  const refocusSearch = searchInput.reclaim;
  // The dialog hands the cursor back to the gear as it closes; the desk's next read belongs in the scan box [SSD-7387].
  const settingsWereOpen = useRef(false);
  useEffect(() => {
    if (settingsWereOpen.current && !settingsOpen) {
      refocusSearch();
    }
    settingsWereOpen.current = settingsOpen;
  }, [settingsOpen, refocusSearch]);

  // The whole page hands the cursor back, not just the View Orders card: the menu bar, the side margins
  // and the empty space under a short order left a desk that clicked there with no scan box [SSD-7051].
  useEffect(() => {
    const handBack = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest(CURSOR_OWNERS)) {
        return;
      }
      // A drag that selected text is someone copying a number, and refocusing would drop the selection.
      if ((window.getSelection()?.toString() ?? '') !== '') {
        return;
      }
      refocusSearch();
    };
    document.addEventListener('click', handBack);
    return () => document.removeEventListener('click', handBack);
  }, [refocusSearch]);

  // The lookup dialog gives focus back on close but not the selection, so the next scan appended [G-1].
  const lookupWasOpen = useRef(false);
  useEffect(() => {
    if (lookupWasOpen.current && lookupCode === null && !sendToPool) {
      refocusSearch();
    }
    lookupWasOpen.current = lookupCode !== null;
  }, [lookupCode, sendToPool, refocusSearch]);

  // A confirm hands focus back to the button that opened it, which the write it agreed to has just disabled:
  // the cursor then sat on nothing and a scan read during the write was lost.
  const confirmOpen = confirmDialog !== null;
  const confirmWasOpen = useRef(false);
  useEffect(() => {
    if (confirmWasOpen.current && !confirmOpen) {
      refocusSearch();
    }
    confirmWasOpen.current = confirmOpen;
  }, [confirmOpen, refocusSearch]);

  const loadComments = useCallback(
    async (orderId: number) => {
      try {
        const response = await client.listOrderComments(orderId);
        setComments(response.comments);
      } catch {
        setComments([]);
      }
    },
    [client],
  );

  /** One request for the whole order; a catalog that is down leaves the columns empty, not the page. */
  const loadCatalog = useCallback(
    async (detail: FulfillmentOrderDetail): Promise<Record<string, ProductLookupResponse>> => {
      const skus = [...new Set(detail.lines.map((line) => line.sku))];
      if (skus.length === 0) {
        return {};
      }
      try {
        // The admin lookup, not the customer-facing one: that route sanitizes the barcode away, so the
        // Barcode column would offer an input for every product the catalog already knows one for.
        const products = await stock.getAdminProductsBySkus(skus);
        const bySku = Object.fromEntries(products.map((product) => [product.sku, product]));
        setCatalog(bySku);
        return bySku;
      } catch {
        setCatalog({});
        return {};
      }
    },
    [stock],
  );

  /** The slug WordPress gives a brand name: lower case, runs of anything but letters and digits as one dash. */
  const brandSlugOf = (brand: string) =>
    brand
      .trim()
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '');

  /**
   * Asks the mapper for each brand on the order not asked before; the order never waits on it. The brand is the one
   * the table shows: the line's own, else the catalog's, since a legacy line carries none.
   */
  const loadBrandKr = useCallback(
    (detail: FulfillmentOrderDetail, products: Record<string, ProductLookupResponse>) => {
      const shown = detail.lines.flatMap((line) => {
        const brand = line.brand ?? products[line.sku]?.brand;
        return brand ? [brand] : [];
      });
      const brands = [...new Set(shown)].filter((brand) => !brandKrAsked.current.has(brand));
      brands.forEach((brand) => brandKrAsked.current.add(brand));
      brands.forEach((brand) => {
        const slug = brandSlugOf(brand);
        if (!slug) {
          return;
        }
        void stock
          .getBrandKr(slug)
          .then((kr) => {
            if (kr) {
              setBrandKr((previous) => (previous[brand] === kr ? previous : { ...previous, [brand]: kr }));
            }
          })
          .catch(() => undefined);
      });
    },
    [stock],
  );

  /** A stock read that fails leaves every waiting line to be typed, never the order unopened. */
  const loadOrder = useCallback(
    async (orderId: number, read?: FulfillmentOrderDetail) => {
      // The thread and the catalog answer different questions than the order does, so none of the
      // three waits on another — a desk action used to pay all three latencies end to end.
      // An order the scan has just read is not read a second time.
      const [detail] = await Promise.all([read ?? client.getOrderDetailForOutbound(orderId), loadComments(orderId)]);
      const sameOrder = shownOrder.current?.header.orderId === detail.header.orderId;
      if (shownOrder.current && !sameOrder) {
        askOnLeaving(shownOrder.current);
      }
      closeShelfAsk(detail);
      shownOrder.current = detail;
      if (returnToShelf.current === detail.header.orderId) {
        returnToShelf.current = null;
        shelfAsked.current = true;
        setShelfFocusFor(detail.header.orderId);
      }
      setOrder(detail);
      setSelected([]);
      if (shelfEdit.current !== detail.header.orderId) {
        shelfEdit.current = null;
        setShelf(detail.header.temporaryLocation ?? '');
        setShelfDirty(false);
        setShelfSaved(false);
      }
      // The order on screen read again after a desk action keeps the products it already has: they did not
      // change, and fetching them again held the action's outcome back by another round trip [SSD-7390].
      const products =
        !sameOrder || detail.lines.some((line) => !catalogRef.current[line.sku])
          ? await loadCatalog(detail)
          : catalogRef.current;
      loadBrandKr(detail, products);
      // Returned so a scan can judge the order it just opened without re-reading state.
      return detail;
    },
    [client, loadCatalog, loadComments, askOnLeaving, closeShelfAsk, loadBrandKr],
  );

  /** An inbound view is not an order: the card and its outbound button left behind would act on a parcel no longer in hand. */
  const clearOrder = () => {
    setParkedHere(null);
    setOrder(null);
    setSearchedTracking(null);
    setSelected([]);
    setComments([]);
    shelfEdit.current = null;
    askOnLeaving(shownOrder.current);
    shownOrder.current = null;
    setShelf('');
    setShelfDirty(false);
    setShelfSaved(false);
    setViolations([]);
    setFailures(NO_REFUSALS);
    setSkippedLines([]);
  };

  /** Writes a product's barcode to the product master; `saved` is true only once the server took it. */
  const writeBarcode = async (
    productId: number,
    barcode: string,
    quiet = false,
  ): Promise<{ saved: boolean; owner?: BarcodeOwner }> => {
    const result = await productAttributesClient.updateBarcode(productId, barcode);
    if (!result.success) {
      // Another product carries the code: the cell shows which one under the box, so no toast says it twice (SSD-7532).
      if (result.owner) {
        FulfillmentSound.failure();
        return { saved: false, owner: result.owner };
      }
      if (result.unpublished) {
        // Nothing was saved and nothing will scan in; the button still receives the line (SSD-7740).
        FulfillmentSound.failure();
        showError(result.message, ['Inbound it with the Inbound button']);
        return { saved: false };
      }
      showFailure(new Error('barcode'), result.message);
      return { saved: false };
    }
    setCatalog((previous) => {
      const entry = Object.values(previous).find((product) => product.productId === productId);
      return entry ? { ...previous, [entry.sku]: { ...entry, barcode } } : previous;
    });
    // A code made with Make code says so once, together with its label print.
    if (!quiet) {
      setToast({ text: `✓ Barcode saved — ${barcode}`, note: 'The next scan of this product resolves' });
    }
    return { saved: true };
  };

  /** Shows the code the product master now has in the cell, which also closes an edit left open over it. */
  const showSavedBarcode = (productId: number, barcode: string | undefined) =>
    setCatalog((previous) => {
      const entry = Object.values(previous).find((product) => product.productId === productId);
      if (!entry) {
        return previous;
      }
      const next: ProductLookupResponse = { ...entry };
      if (barcode) {
        next.barcode = barcode;
      } else {
        delete next.barcode;
      }
      return { ...previous, [entry.sku]: next };
    });

  /**
   * Saves a typed or scanned barcode [SSD-7912]. The save overwrites, and the screen's catalog can be hours old, so
   * the product is read again first, as Make code does (SSD-7611): a barcode saved elsewhere since `shown` (the one
   * the cell showed) is kept, and the cell shows it. `shown` is what an intentional replace was confirmed against.
   */
  const saveBarcode = async (
    productId: number,
    barcode: string,
    sku: string,
    shown: string | undefined,
  ): Promise<BarcodeOwner | undefined> => {
    let current: ProductLookupResponse | undefined;
    try {
      const products = await stock.getAdminProductsBySkus([sku], { fresh: true });
      current = products.find((product) => product.sku === sku && product.productId === productId);
      if (!current) {
        FulfillmentSound.failure();
        showError(
          `${sku} could not be matched to this product any more, so the barcode was not saved. Reload the order.`,
        );
        return undefined;
      }
    } catch (error) {
      console.error('Barcode save: product re-read failed', error);
      showFailure(error, 'Could not check this product’s barcode, so it was not saved. Save it again.');
      return undefined;
    }
    const existing = current.barcode?.trim() || undefined;
    const onScreen = shown?.trim() || undefined;
    // Another screen saved this very code: the product is already right [SSD-7912].
    if (existing === barcode) {
      showSavedBarcode(productId, existing);
      setToast({ text: `✓ Barcode already saved — ${barcode}`, note: 'The next scan of this product resolves' });
      return undefined;
    }
    // The barcode changed elsewhere after the screen loaded, so it is not overwritten, even by a confirmed replace.
    // A barcode removed elsewhere counts too: the desk sees the empty cell and saves again if the code is right.
    if (existing !== onScreen) {
      showSavedBarcode(productId, existing);
      FulfillmentSound.failure();
      showError(
        existing
          ? `${sku} already has barcode ${existing}, so ${barcode} was not saved.`
          : `${sku}'s barcode ${onScreen} was removed elsewhere after this page loaded, so ${barcode} was not saved. Save it again if it is right.`,
      );
      return undefined;
    }
    return (await writeBarcode(productId, barcode)).owner;
  };

  /**
   * Gives a product with no barcode its internal code and prints one sticker for it (SSD-7611). The sticker
   * prints only once the server has taken the code, so no label goes on a product with a code that does not scan.
   */
  const makeBarcode = async (
    productId: number,
    code: string,
    sku: string,
    names: LabelNames,
  ): Promise<BarcodeOwner | undefined> => {
    // The screen's catalog can be hours old, and the save overwrites, so a barcode added elsewhere since is read first.
    let current: ProductLookupResponse | undefined;
    try {
      const products = await stock.getAdminProductsBySkus([sku], { fresh: true });
      current = products.find((product) => product.sku === sku && product.productId === productId);
      if (!current) {
        FulfillmentSound.failure();
        showError(`${sku} could not be matched to this product any more, so no code was made. Reload the order.`);
        return undefined;
      }
    } catch (error) {
      console.error('Make code: product re-read failed', error);
      showFailure(error, 'Could not check this product’s barcode, so no code was made. Try Make code again.');
      return undefined;
    }
    const existing = current.barcode?.trim();
    if (existing) {
      setCatalog((previous) => {
        const entry = Object.values(previous).find((product) => product.productId === productId);
        return entry ? { ...previous, [entry.sku]: { ...entry, barcode: existing } } : previous;
      });
      // Another desk made this very code: the product is already right and only this unit still needs its sticker.
      if (existing === code) {
        await printCodeLabel(code, sku, names, `✓ Label printed — ${code} was already saved`);
        return undefined;
      }
      FulfillmentSound.failure();
      showError(`${sku} already has barcode ${existing}, so no code was made and nothing printed.`);
      return undefined;
    }
    const result = await writeBarcode(productId, code, true);
    if (!result.saved) {
      return result.owner;
    }
    await printCodeLabel(code, sku, names, `✓ Barcode saved and label printed — ${code}`);
    return undefined;
  };

  /**
   * The sticker's names: brand and name in English, and in Korean when the line or the catalog knows the Korean
   * name (FC request). The Korean brand stands in for the English one once the mapper has answered.
   */
  const labelNamesOf = (
    line: { productName?: string | null; productNameKr?: string | null },
    brand: string | null | undefined,
    catalogNameKr: string | undefined,
  ): LabelNames => {
    const nameKr = line.productNameKr || catalogNameKr;
    const brandName = brand ? (brandKr[brand] ?? brand) : undefined;
    // Korean names usually open with the brand already; the sticker says it once.
    const withBrand = nameKr && brandName && !nameKr.startsWith(brandName) ? `${brandName} ${nameKr}` : nameKr;
    return {
      name: [brand, line.productName].filter(Boolean).join(' ') || undefined,
      nameKr: withBrand || undefined,
    };
  };

  /**
   * Make code and Print label are buttons, which the page's click hand-back leaves alone, so after them the cursor
   * sat on the vanished button and the sticker's scan went nowhere (FC desk test, 2026-10-23). The cursor goes to the
   * scan box on the click, as the order prints do, and is put back after the save and the print unless the desk
   * moved to another field meanwhile. Anything that is not a field takes it back: the body, the print frame, or a
   * browser extension's overlay (a password manager took the cursor on dev, 2026-10-23).
   */
  const withScanCursor = async <T,>(work: () => Promise<T>): Promise<T> => {
    refocusSearch();
    const parked = document.activeElement;
    try {
      return await work();
    } finally {
      const cursor = document.activeElement;
      const anotherField =
        cursor !== parked &&
        (cursor instanceof HTMLInputElement ||
          cursor instanceof HTMLTextAreaElement ||
          cursor instanceof HTMLSelectElement ||
          (cursor instanceof HTMLElement && cursor.isContentEditable));
      if (!anotherField) {
        refocusSearch();
      }
    }
  };

  /** One code label onto the label printer; a failure names the printer, as every other print on this desk does. */
  const printCodeLabel = async (code: string, sku: string, names: LabelNames, done: string) => {
    let job: PrintJob;
    try {
      job = productCodeLabelJob({ code, sku, ...names });
    } catch (cause) {
      setToast({
        text: `The label for ${code} could not be drawn: ${cause instanceof Error ? cause.message : String(cause)}`,
        failed: true,
      });
      return;
    }
    reportPrint(await printer.dispatch(job), done);
  };

  /** Which line of the order on screen a product barcode belongs to; one still waiting outranks one already in. */
  const findScannedLine = (sku: string): ScannedProductLine | undefined => {
    const waiting = lines.find((line) => line.sku === sku && canInboundLine(line.lineStatus));
    if (waiting) {
      return { lineNo: waiting.lineNo, arrived: false };
    }
    return arrivedMatchFor(lines, sku);
  };

  /** Every sheet this screen produces goes through the one dispatcher, which prints it at once. */
  const printer = useMemo(() => new PrintDispatcher(), []);

  /** Deleo-family stickers are drawn here, as on Order Detail, instead of fetched from the label archive. */
  const deleoLabelRef = useRef<HTMLDivElement>(null);
  /** The Deleo-family print in flight: react-to-print reports its end through callbacks, not a promise. */
  const deleoPrinting = useRef<{
    resolve: () => void;
    reject: (cause: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const settleDeleoPrint = (cause?: Error) => {
    const printing = deleoPrinting.current;
    deleoPrinting.current = null;
    if (printing) {
      clearTimeout(printing.timer);
    }
    if (cause) {
      printing?.reject(cause);
    } else {
      printing?.resolve();
    }
  };
  const printDeleoLabel = useReactToPrint({
    contentRef: deleoLabelRef,
    pageStyle: DELEO_LABEL_PAGE_STYLE,
    onAfterPrint: () => settleDeleoPrint(),
    // [E-39] the dialog reports its own failures asynchronously, so they reach the dispatcher through here.
    onPrintError: () => settleDeleoPrint(new Error('Carrier label did not print')),
  });
  /**
   * react-to-print prints a beat later in a frame of its own; this settles only once that print is over,
   * so the PACKING that follows on the same click comes out after the carrier label, not before it.
   */
  const printDeleoLabelThrough = async (orderId: number): Promise<void> => {
    // A scan prints the order it has only just opened, so its sticker may be a render away; the one still
    // drawn for the order before is never what goes to the printer [SSD-7415].
    const drawn = () => deleoLabelRef.current?.dataset['orderId'] === String(orderId);
    for (let wait = 0; wait < DELEO_DRAW_WAITS && !drawn(); wait += 1) {
      await new Promise((resolve) => setTimeout(resolve, DELEO_DRAW_WAIT_MS));
    }
    // With nothing drawn react-to-print calls neither callback, and the click would never finish.
    if (!drawn()) {
      throw new Error('The Deleo waybill was not drawn');
    }
    // A print still in flight is not going to report to this click; it ends as a failure, not a hang.
    settleDeleoPrint(new Error('Carrier label did not print'));
    return new Promise<void>((resolve, reject) => {
      // Some react-to-print failures call neither callback; the click must still end and the PACKING print.
      const timer = setTimeout(
        () => settleDeleoPrint(new Error('The carrier label print did not report back')),
        DELEO_PRINT_TIMEOUT_MS,
      );
      deleoPrinting.current = { resolve, reject, timer };
      printDeleoLabel();
    });
  };

  /** A refused print names the printer, because that is the thing the desk has to go and look at. */
  const reportPrint = (outcome: PrintOutcome, done: string) => {
    if (outcome.ok) {
      setToast({ text: done });
      return;
    }
    setToast({ text: PrintFailureNotice.textOf(outcome), failed: true });
  };

  /** One outcome onto the feedback stack, on the component the other desk screens render [T03]. */
  const setToast = (outcome: Toast) => {
    // The note is a second line about the same outcome, which is what the detail list is for.
    const details = outcome.note ? [outcome.note] : undefined;
    // An outcome the screen already marked as not-a-success must not reach the desk in the success colour.
    const show = outcome.failed === 'partial' ? showWarning : outcome.failed ? showError : showSuccess;
    show(outcome.text, details);
  };

  const reportFailure = (err: unknown) => {
    // "Action failed" told the desk nothing it could act on; the server's own words replace it.
    showFailure(err, GENERIC_ERROR);
    FulfillmentSound.failure();
  };

  /**
   * The cursor goes where the next scan belongs when the button is clicked, not once the save is back
   * [SSD-7226]: the clicked button is disabled for the 1-2 s of the save, and a scan read then used to land
   * on the body and be lost. After the save it is put back only if it is still where the click sent it or was
   * dropped; a desk that moved on meanwhile (a slot saved into the Shelf hands the cursor to the scan box)
   * keeps its place. A refused action hands the cursor to the scan box, where the retry is read.
   */
  const settleCursor = (landed: boolean, parked: Element | null, focusAfter: () => void) => {
    if (!landed) {
      refocusSearch();
      return;
    }
    const cursor = document.activeElement;
    if (cursor === parked || cursor === null || cursor === document.body) {
      focusAfter();
    }
  };

  const runAction = async (
    orderId: number,
    action: () => Promise<unknown>,
    done: Toast | ((detail: FulfillmentOrderDetail) => Toast),
    focusAfter: () => void = refocusSearch,
  ): Promise<boolean> => {
    beginAction();
    setViolations([]);
    focusAfter();
    const parked = document.activeElement;
    let landed = false;
    try {
      await action();
      const detail = await loadOrder(orderId);
      setToast(typeof done === 'function' ? done(detail) : done);
      landed = true;
      return true;
    } catch (err) {
      const failure = failureOf(err);
      const blocked = failure?.response?.status === 409 ? failure.response.data?.violations : undefined;
      if (Array.isArray(blocked)) {
        setViolations(blocked);
        // The gates are itemised in their own banner below; repeating them here would say it twice.
        showFailure(err, 'The action was refused — see the gates below');
      } else {
        showFailure(err, GENERIC_ERROR);
      }
      FulfillmentSound.failure();
      return false;
    } finally {
      endAction();
      settleCursor(landed, parked, focusAfter);
    }
  };

  /** Starts an outbound-side write unless one is already out; whoever started it ends it once the order is read back. */
  const beginOutboundWork = (work: OutboundWork): boolean => {
    if (outboundWorkRef.current) {
      return false;
    }
    outboundWorkRef.current = work;
    setOutboundWork(work);
    return true;
  };
  const endOutboundWork = () => {
    outboundWorkRef.current = null;
    setOutboundWork(null);
  };
  /** The outbound chime only for an order that read back as gone; one still in the warehouse is a warning. */
  const soundOutbound = () => {
    if (shownOrder.current && hasLeft(shownOrder.current)) {
      FulfillmentSound.success();
    } else {
      FulfillmentSound.warning();
    }
  };

  /** The slot every JIT line of this order is received onto: the order's saved Shelf [SSD-7226 D3]. */
  const shelfLocation = (on: FulfillmentOrderDetail | null = order): string =>
    (on?.header.temporaryLocation ?? '').trim();

  /**
   * Where a line is booked. An existing-inventory line already holds a reservation on its own shelf; a JIT
   * line goes onto the order's Shelf, and while the order has none it goes through the packing desk, so a
   * receipt never waits on the slot scan. The Location column only shows this; it is edited on Current
   * Stocks, never here. A scan that has just opened an order reads that order, not the one this render shows.
   */
  const lineLocationOf = (line: FulfillmentOrderLine, on?: FulfillmentOrderDetail): string =>
    (on ? lineLocations(on.reservations) : locations).get(line.lineNo) ?? (shelfLocation(on) || PACKING_LOCATION);

  /** Every line to inbound with the location it goes to [SSD-7226 D3]. */
  const planLocations = (targets: FulfillmentOrderLine[]): Array<{ lineNo: number; locationCode: string }> =>
    targets.map((line) => ({ lineNo: line.lineNo, locationCode: lineLocationOf(line) }));

  /**
   * One bulk call per location, because the wire takes one location per call and one shelf holds one
   * product. A location the server refused outright refuses only its own lines; when every call failed,
   * the first failure is the answer, exactly as a single call's would be.
   */
  const inboundByLocation = async (orderId: number, planned: Array<{ lineNo: number; locationCode: string }>) => {
    let inbounded = 0;
    const refused: BulkInboundFailure[] = [];
    const passedOver: BulkInboundFailure[] = [];
    let unconfirmed = 0;
    const errors: unknown[] = [];
    const groups = groupByLocation(planned);
    for (const [locationCode, lineNos] of groups) {
      try {
        const result = await client.bulkInboundLines(orderId, { lineNos, locationCode });
        if (!isCompleteInboundBulkResult(result, lineNos)) {
          unconfirmed += lineNos.length;
          continue;
        }
        inbounded += result.inbounded;
        refused.push(...result.failures);
        passedOver.push(...result.skipped);
      } catch (err) {
        errors.push(err);
        refused.push(...lineNos.map((lineNo) => ({ lineNo, reason: describeFailure(err, GENERIC_ERROR) })));
      }
    }
    if (errors.length === groups.size) {
      throw errors[0];
    }
    return { inbounded, refused, passedOver, unconfirmed };
  };

  /**
   * Returns where the goods went, so a scan-driven receipt can say so on its banner. `focusAfter` is where
   * the cursor goes at click time (the Shelf for a clicked Inbound); a scan-driven receipt keeps the scan box.
   * `on` is the order a scan has just opened, read before this render shows it [SSD-7413].
   */
  const inboundOne = async (
    orderId: number,
    line: FulfillmentOrderLine,
    alsoOutbound: boolean,
    focusAfter: () => void = refocusSearch,
    on?: FulfillmentOrderDetail,
  ): Promise<string | undefined> => {
    if (on ? isCancelledOrder(on.header) : orderCancelled) {
      showFailure(new Error('cancelled'), `Order ${orderId} is cancelled — ${line.sku} cannot be inbounded.`);
      FulfillmentSound.failure();
      return undefined;
    }
    const carrierBlock = outboundCarrierBlock(orderId, (on ?? order)?.tracking, (on ?? order)?.pendingParcels);
    if (alsoOutbound && carrierBlock) {
      showFailure(new Error(carrierBlock), carrierBlock);
      return undefined;
    }
    // Onto the order's Shelf, or through the packing desk while the order has none.
    const locationCode = lineLocationOf(line, on);
    const noShelf = !shelfLocation(on ?? order);
    if (alsoOutbound && !beginOutboundWork({ orderId, kind: 'inbound-outbound' })) {
      return undefined;
    }
    let done = false;
    try {
      done = await runAction(
        orderId,
        async () => {
          await client.inboundLine(orderId, line.lineNo, { locationCode });
          // Sequential by design: the order can only leave once its last line is in.
          if (alsoOutbound) {
            await client.outboundOrder(orderId);
          }
        },
        alsoOutbound
          ? (detail) => outboundToast(detail, 'Inbound + Outbound')
          : {
              text: `✓ Inbound complete — ${line.sku} · order ${orderId} line ${line.lineNo}`,
              note: 'No refresh · ready for the next scan',
            },
        focusAfter,
      );
    } finally {
      if (alsoOutbound) {
        endOutboundWork();
      }
    }
    if (done && alsoOutbound) {
      soundOutbound();
      releaseMatched({ orderId });
    } else if (done && noShelf) {
      // The parcel is in hand with no slot, so the cursor waits in the Shelf for that scan.
      shelfAsked.current = true;
      focusShelf();
    }
    return done ? locationCode : undefined;
  };

  /**
   * After an Inbound click the parcel goes to the rebin station, so the cursor waits in the Shelf with its
   * value selected and the slot scanned there overwrites it [SSD-7052]. After an Outbound the Shelf stays as
   * it was — the desk presses Outbound and then reads the slot off the line to go and fetch the parcel — so
   * an outbound hands the cursor to the scan box instead, where the next parcel is read [SSD-7226].
   */
  const focusShelf = () => {
    const input = shelfRef.current;
    if (!input) {
      return;
    }
    input.focus({ preventScroll: true });
    input.select();
  };

  // "Go back" reopened this order for its slot: once it is on screen, the cursor waits in its Shelf.
  useEffect(() => {
    if (shelfFocusFor !== null && order?.header.orderId === shelfFocusFor) {
      setShelfFocusFor(null);
      shelfRef.current?.focus({ preventScroll: true });
      shelfRef.current?.select();
    }
  }, [order, shelfFocusFor]);

  /**
   * A barcode scan names the product, not how many came, so a line of two or more asks first (SSD-7677). The same
   * count receives the line; more, nothing or not a number refuses; fewer points to Split line, since the line
   * would otherwise go in whole. The buttons receive the whole line without asking, as they always have.
   */
  const scannedQuantityMatches = async (target: FulfillmentOrderLine): Promise<boolean> => {
    const ordered = target.totalQuantity;
    if (!(ordered > 1)) {
      return true;
    }
    const answer = await ask({
      title: 'How many arrived?',
      label: `${target.sku} · line ${target.lineNo} · ordered ${ordered}`,
      confirmLabel: 'Inbound',
    });
    if (answer === null) {
      return false;
    }
    // The box keeps the cursor, so the next product's scan can land in it: a barcode is not a count.
    if (answer.length >= 6) {
      showWarning(`${answer} looks like a barcode, not a count — nothing was inbounded`, [
        `Scan ${target.sku} again and type how many arrived`,
      ]);
      FulfillmentSound.failure();
      return false;
    }
    const count = /^\d+$/.test(answer) ? Number(answer) : NaN;
    if (!Number.isInteger(count) || count === 0) {
      showWarning(`"${answer}" is not a count — nothing was inbounded`, ['Scan the product again']);
      FulfillmentSound.failure();
      return false;
    }
    if (count > ordered) {
      showWarning(`${count} is more than the ${ordered} ordered of ${target.sku} — nothing was inbounded`, [
        'Check with the order team',
      ]);
      FulfillmentSound.failure();
      return false;
    }
    if (count < ordered) {
      showWarning(`Only ${count} of ${ordered} ${target.sku} arrived — nothing was inbounded`, [
        'Split the line with Split line in Order Detail, then scan again',
      ]);
      FulfillmentSound.failure();
      return false;
    }
    return true;
  };

  /**
   * The scan path's receipt (SSD-7677): the count first, then the line goes in, and the last waiting line goes out
   * with it the way the Inbound + Outbound button does. A hold, a carrier block or a customer return keeps it to the
   * inbound and says why, and an outbound refused after the inbound landed says that the line is in.
   */
  const receiveScannedLine = async (asked: FulfillmentOrderDetail, askedLine: FulfillmentOrderLine) => {
    if (!(await scannedQuantityMatches(askedLine))) {
      return;
    }
    // A count waited on the desk, and another desk may have moved the order meanwhile: decide on a fresh read.
    const detail = askedLine.totalQuantity > 1 ? await loadOrder(asked.header.orderId) : asked;
    const target = detail.lines.find((other) => other.lineNo === askedLine.lineNo);
    if (!target || !canInboundLine(target.lineStatus) || isCancelledOrder(detail.header)) {
      showWarning(`Order ${detail.header.orderId} changed while it waited — nothing was inbounded`, [
        'Scan the product again',
      ]);
      FulfillmentSound.failure();
      return;
    }
    const orderId = detail.header.orderId;
    const waitingNow = detail.lines.filter((other) => canInboundLine(other.lineStatus));
    const last = waitingNow.length === 1 && waitingNow[0]?.lineNo === target.lineNo;
    const blockedBy = detail.header.holdReason
      ? `order ${orderId} is on hold`
      : outboundCarrierBlock(orderId, detail.tracking, detail.pendingParcels);
    const alsoOutbound = last && !blockedBy && !returnMode;
    const locationCode = await inboundOne(orderId, target, alsoOutbound, refocusSearch, detail);
    if (locationCode) {
      setBanner((previous) => (previous ? { ...previous, receivedAt: locationCode } : previous));
      // PB-7: the last bought product scanned in completes the order, so its label prints now (SSD-7389).
      // inboundOne read the order back, and that read is what carries a label the carrier issued on receipt.
      if (autoPrintOnScan && !returnMode && lastToReceive(detail, target)) {
        const readBack = shownOrder.current;
        autoPrint(readBack && readBack.header.orderId === orderId ? readBack : detail);
      }
      if (last && returnMode) {
        showWarning(`Inbounded ${target.sku} — not outbounded: this parcel came back as a customer return`);
      } else if (last && blockedBy) {
        showWarning(`Inbounded ${target.sku} — not outbounded: ${blockedBy}`, ['Outbound it once that is cleared']);
      }
      return;
    }
    if (alsoOutbound) {
      // The inbound and the outbound are two writes: the line may be in although the outbound was refused.
      const reread = await loadOrder(orderId).catch(() => null);
      const now = reread?.lines.find((other) => other.lineNo === target.lineNo);
      const left = reread?.header.fulfillmentStatus === 'OUTBOUNDED' || reread?.header.fulfillmentStatus === 'SHIPPED';
      if (now && !left && !canInboundLine(now.lineStatus)) {
        showWarning(`Inbounded ${target.sku}, but order ${orderId} was not outbounded — see the reason above`, [
          'Press Outbound once it is fixed',
        ]);
      }
    }
  };

  /** Opens the product picker and waits for the desk's answer; null when it was closed. */
  const pickBarcodeProduct = (code: string, options: AssignBarcodeOption[], initial?: number) =>
    new Promise<BarcodePickAnswer>((settle) => setAssignAsk({ code, options, initial, settle }));

  /**
   * A code no product carries, scanned with an order open, is most often the barcode of a product in this parcel
   * that was never registered (SSD-7677). The desk picks the product; the code is saved as its barcode (the server
   * refuses a code another product has) and the line is received as any scan would receive it.
   */
  const assignScannedBarcode = async (detail: FulfillmentOrderDetail, code: string): Promise<boolean> => {
    const waitingNow = detail.lines.filter((other) => canInboundLine(other.lineStatus));
    // A UPC-A and the EAN-13 that is it with a leading 0 are one product number, and the desk scanner drops that 0
    // (SSD-7677). A product on this order that carries the other form is the product in hand, whether its line is
    // still waiting or already in: nothing is saved, so its twin never lands on another product, and the scan reads
    // as that product's own barcode would.
    const twinCode =
      code.length === 12 ? `0${code}` : code.length === 13 && code.startsWith('0') ? code.slice(1) : null;
    // A twin can only be ruled out against every product on the order: one the catalog did not return could be it,
    // so nothing is offered for saving until the screen has read them all.
    if (twinCode && detail.lines.some((other) => !catalog[other.sku])) {
      showWarning(`${code} was not saved — this order's products have not all loaded`, [
        'Reload the order and scan again, or inbound with the button',
      ]);
      FulfillmentSound.failure();
      return true;
    }
    const twinSkus = twinCode
      ? [...new Set(detail.lines.filter((other) => catalog[other.sku]?.barcode === twinCode).map((other) => other.sku))]
      : [];
    if (twinSkus.length > 1) {
      // More than one product on the order carries the other form: only the desk can tell which one is in hand.
      showWarning(`${code} matches more than one product on this order — nothing was saved or inbounded`, [
        'Check the product in hand and inbound it with its line button',
      ]);
      FulfillmentSound.failure();
      return true;
    }
    const twinSku = twinSkus[0];
    if (twinSku) {
      const waiting = waitingNow.find((other) => other.sku === twinSku);
      const match = waiting ? { lineNo: waiting.lineNo, arrived: false } : arrivedMatchFor(detail.lines, twinSku);
      setBanner({
        kind: 'PRODUCT_BARCODE',
        ...(match && { orderId: detail.header.orderId }),
        commentReadId: ++commentReadSequence.current,
        sku: twinSku,
        match,
      });
      if (waiting) {
        await receiveScannedLine(detail, waiting);
      } else if (match?.fromShelf) {
        FulfillmentSound.success();
      } else if (match?.arrived) {
        FulfillmentSound.warning();
      } else {
        FulfillmentSound.failure();
      }
      return true;
    }
    const options: AssignBarcodeOption[] = waitingNow.map((other) => {
      const product = catalog[other.sku];
      const brand = other.brand ?? product?.brand;
      return {
        lineNo: other.lineNo,
        sku: other.sku,
        name: [brand, other.productName].filter(Boolean).join(' ') || other.sku,
        image: other.imageUrl ?? product?.productImageUrls?.[0],
        barcode: product?.barcode || undefined,
      };
    });
    const bare = options.filter((option) => !option.barcode);
    FulfillmentSound.warning();
    const lineNo = await pickBarcodeProduct(code, options, bare.length === 1 ? bare[0]?.lineNo : undefined);
    if (lineNo === 'not-product') {
      // A waybill or another code the desk recognises: it goes down the dead end it always took.
      return false;
    }
    if (lineNo === null) {
      showWarning(`${code} was not saved — nothing was inbounded`, ['Inbound with the button, or scan again']);
      return true;
    }
    if (typeof lineNo === 'object') {
      // The scanner fired while the picker was open; its Enter must not pick or close anything for the desk.
      showWarning(`A scan (${lineNo.scanned}) came in while the picker was open — nothing was saved`, [
        `Scan ${code} again, then pick its product`,
      ]);
      FulfillmentSound.failure();
      return true;
    }
    const target = waitingNow.find((other) => other.lineNo === lineNo);
    const productId = target ? catalog[target.sku]?.productId : undefined;
    if (!target || productId == null) {
      showWarning(`${target?.sku ?? 'This product'} is not in the catalog yet — ${code} was not saved`, [
        'Inbound with the button',
      ]);
      FulfillmentSound.failure();
      return true;
    }
    const saved = await writeBarcode(productId, code, true);
    if (!saved.saved) {
      if (saved.owner) {
        showWarning(
          `${code} already belongs to ${saved.owner.name ?? `product ${saved.owner.productId}`} — not saved`,
          ['Check the product in hand'],
        );
      }
      return true;
    }
    setToast({
      text: `✓ Barcode saved — ${code} is ${target.sku} now`,
      note: 'Next time this product scans straight in',
    });
    // The picked line shows its OMS comments as a scanned one does [SSD-7314].
    setBanner({
      kind: 'PRODUCT_BARCODE',
      orderId: detail.header.orderId,
      commentReadId: ++commentReadSequence.current,
      sku: target.sku,
      match: { lineNo: target.lineNo, arrived: false },
    });
    // The pick waited on the desk, so the line is received against the order as it stands now.
    const fresh = await loadOrder(detail.header.orderId);
    const freshLine = fresh.lines.find((other) => other.lineNo === target.lineNo);
    if (!freshLine || !canInboundLine(freshLine.lineStatus) || isCancelledOrder(fresh.header)) {
      showWarning(`${code} is saved, but line ${target.lineNo} is no longer waiting — nothing was inbounded`);
      FulfillmentSound.failure();
      return true;
    }
    await receiveScannedLine(fresh, freshLine);
    return true;
  };

  const inboundClicked = async (orderId: number, line: FulfillmentOrderLine) => {
    const alsoOutbound = !held && !carrierOutboundBlock && lastPending?.lineNo === line.lineNo;
    // Inbound -> Shelf, Inbound + Outbound -> scan box, both from the click [SSD-7052] [SSD-7226].
    await inboundOne(orderId, line, alsoOutbound, alsoOutbound ? refocusSearch : focusShelf);
  };

  /**
   * Pulls the whole request, not the parcel: the desk counts every line the request carries [L-S6-5].
   * A read overtaken by another read, or by the desk leaving for an order, is dropped: landing late it would
   * put back a request the next product scan is then counted into.
   */
  const loadInbound = async (docId: string): Promise<InboundRequestDetail | undefined> => {
    if (inboundLocationsDocId.current !== docId) {
      clearInbound();
    }
    const seq = ++inboundReadSeq.current;
    const detail = await client.getInboundRequest(docId);
    if (seq !== inboundReadSeq.current) {
      return undefined;
    }
    // Locations typed for one request are that request's; a re-read of it keeps them, another starts clean.
    const sameRequest = inboundLocationsDocId.current === detail.doc.docId;
    inboundLocationsDocId.current = detail.doc.docId;
    setInbound(detail);
    setInboundRefreshStale(null);
    setInboundLocations((previous) => {
      const merged = sameRequest ? { ...previous } : {};
      for (const line of detail.lines) {
        // What a line shows is what its confirm sends: a shelf the server now supplies replaces one left empty.
        const shown = shownShelf(line);
        if (shown !== '') {
          merged[line.sku] = shown;
        } else if (merged[line.sku] == null) {
          merged[line.sku] = '';
        }
      }
      return merged;
    });
    return detail;
  };

  /** An order on screen is not a request: a count left claiming product scans would count the order's goods. */
  const clearInbound = () => {
    inboundReadSeq.current += 1;
    inboundLocationsDocId.current = null;
    setInbound(null);
    setInboundLocations({});
    setLastCount(null);
    setAutoConfirm(null);
  };

  /** Opens one inbound request on the receive screen, whatever led the desk to it. */
  const openInbound = async (docId: string, inboundNo: string | undefined) => {
    clearOrder();
    setBanner({ kind: 'INBOUND_REQUEST', docId, ...(inboundNo && { inboundNo }) });
    setInboundDone(null);
    await loadInbound(docId);
  };

  /**
   * Leaves the open request with no question asked: every count is already on the server scan by scan, so
   * a confirm has nothing to protect, and the dock answers it with a scanner in hand [SSD-7204 D3].
   */
  const closeInbound = () => {
    clearInbound();
    setBanner(null);
    setCode('');
    refocusSearch();
    forgetView();
  };

  goBackTo.current = (orderId: number) => {
    // A count in progress refuses an order number, so the request is left first, as its own Close does.
    if (inbound) {
      closeInbound();
    }
    returnToShelf.current = orderId;
    setCode(String(orderId));
    queueSearch(String(orderId));
  };

  const runInboundAction = async (action: () => Promise<unknown>, done: Toast) => {
    beginAction();
    try {
      await action();
      setToast(done);
      return true;
    } catch (err) {
      reportFailure(err);
      return false;
    } finally {
      endAction();
      // A cursor working the lines (the next count, a shelf) stays there; any other is given back to the scan box.
      const active = document.activeElement;
      if (!(active instanceof HTMLElement && active.closest(INBOUND_LINES_TABLE))) {
        refocusSearch();
      }
    }
  };

  const confirmFullInbound = async (docId: string, lines: InboundRequestDetail['lines']) => {
    setConfirming(true);
    const done = await runInboundAction(
      async () => {
        // Stock may not enter inventory location-less, so the locations land before the confirm [L-S6-7].
        await client.assignInboundLocations(docId, {
          assignments: lines.map((line) => ({
            sku: line.sku,
            locationCode: (inboundLocations[line.sku] ?? '').trim(),
          })),
        });
        await client.confirmInbound(docId, { mode: 'FULL' });
      },
      { text: '✓ Full inbound confirmed', note: 'Scan the next tracking number' },
    ).finally(() => setConfirming(false));
    if (done) {
      setInboundDone(inbound?.doc.inboundNo ?? docId);
      // The request stays on screen, received: a count typed wrong is corrected right here, not found again.
      try {
        await loadInbound(docId);
      } catch (err) {
        // Received on the server but unread here: the pre-confirm totals would still offer edits and a confirm.
        if (inboundLocationsDocId.current === docId) {
          setInboundRefreshStale('confirm');
        }
        reportFailure(err);
      }
      setCode('');
      await continueMatched({ docId });
    }
  };

  /**
   * The confirm a whole request runs on its own. The request must still be whole and idle here, and the
   * server has the last word: a unit another desk counted meanwhile shows up in the re-read, and a request
   * that is no longer whole is left on screen with its hint instead of being confirmed. The lines are locked
   * from the re-read on, so no second figure can be typed against a confirm already on its way.
   */
  const fireAutoConfirm = useRef<() => void>(() => undefined);
  fireAutoConfirm.current = () => {
    if (!inbound || !inboundWhole || busy) {
      return;
    }
    const { docId } = inbound.doc;
    setConfirming(true);
    void (async () => {
      let fresh: InboundRequestDetail | undefined;
      beginAction();
      try {
        fresh = await loadInbound(docId);
      } catch (err) {
        reportFailure(err);
        return;
      } finally {
        endAction();
      }
      if (!fresh || !linesWhole(fresh.lines, inboundLocations)) {
        return;
      }
      await confirmFullInbound(docId, fresh.lines);
    })().finally(() => setConfirming(false));
  };

  /**
   * A typed count is sent as the difference from the total the desk read, and the server adds it to its
   * own running total. Two stations entering 2 and 3 therefore reach 5 whatever order they save in, and a
   * negative difference still corrects the desk downward [L-S6-6].
   */
  const setInboundReceived = async (docId: string, lineNo: number, raw: string | number): Promise<boolean> => {
    if (inboundRefreshStale) {
      return false;
    }
    const qty = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isInteger(qty) || qty < 0) {
      return false;
    }
    const seen = inbound?.lines.find((line) => line.lineNo === lineNo)?.qtyReceived;
    if (seen === undefined) {
      return false;
    }
    let saved = false;
    const toast: Toast = { text: '✓ Saved', note: 'Counted by hand' };
    await runInboundAction(async () => {
      const edited = await client.setInboundLineReceived(docId, lineNo, { delta: qty - seen });
      saved = true;
      // A received line counted lower came back out of stock; the request is open again either way.
      if (edited.uncounted) {
        const short = edited.reservedShort
          ? ` · ${edited.reservedShort} of them ${edited.reservedShort === 1 ? 'was' : 'were'} reserved for orders, which are now short`
          : '';
        toast.note = `${edited.uncounted} unit${edited.uncounted === 1 ? '' : 's'} taken back out of stock — the request is open again${short}`;
      } else if (edited.status === INBOUND_STATUS.PARTIAL && inbound?.doc.status === INBOUND_STATUS.RECEIVED) {
        toast.note = 'The request is open again';
      }
      // The desk may have left for another request while this count was saving: the count is the
      // server's, and the request it belongs to is not pulled back on screen to show it.
      if (inboundLocationsDocId.current !== docId) {
        return;
      }
      try {
        await loadInbound(docId);
      } catch (err) {
        if (inboundLocationsDocId.current === docId) {
          setInboundRefreshStale('count');
        }
        throw err;
      }
      askWholeCheck();
    }, toast);
    return saved;
  };

  /** Corrects what the paperwork claimed, which re-derives the confirm gate [L-S6-9]. */
  /** Saves what M6 collected; the modal has already refused a figure the server would reject [L-M6]. */
  const saveExpectedQty = async (
    target: EditExpectedQtyTarget,
    qty: number,
    reason: InboundExpectedEditReason,
    memo: string,
  ) => {
    setExpectedEdit(null);
    // A dialog opened before the request went unread would save against totals the screen no longer trusts.
    if (inboundRefreshStale !== null) {
      return;
    }
    const done = await runInboundAction(
      () =>
        client.setInboundLineExpected(target.docId, target.lineNo, {
          qty,
          reason,
          ...(memo.trim().length > 0 && { memo: memo.trim() }),
          ...(target.updatedAt && { expectedUpdatedAt: target.updatedAt }),
        }),
      { text: '✓ Expected qty saved', note: 'The requester is notified on the request' },
    );
    await loadInbound(target.docId);
    // Fewer arrived than the paperwork said: the expectation lowered to the count is the short delivery's
    // way in — when the server took it. A refused edit changed nothing and starts nothing.
    if (done) {
      askWholeCheck();
    }
  };

  // The confirm is due only from the moments listed on `wholeCheck`, and only while the request is whole.
  const lastWholeCheck = useRef(0);
  useEffect(() => {
    if (wholeCheck === lastWholeCheck.current) {
      return;
    }
    lastWholeCheck.current = wholeCheck;
    setAutoConfirm(inboundWhole && inbound ? { docId: inbound.doc.docId } : null);
  }, [wholeCheck, inboundWhole, inbound]);

  // Due, and the save that made the request whole has landed: the request confirms itself at once. A scan
  // already waiting its turn goes first — it may be one more unit, and the count comes before the confirm.
  useEffect(() => {
    if (!autoConfirm || busy || scanning || scanQueue.length > 0) {
      return;
    }
    setAutoConfirm(null);
    if (inbound && inbound.doc.docId === autoConfirm.docId && inboundWhole) {
      fireAutoConfirm.current();
    }
  }, [autoConfirm, busy, scanning, scanQueue, inbound, inboundWhole]);

  /** Another parcel's waybill is a routing question, not a unit on the shelf: counting one inflated the arrival. */
  const isTrackingScan = (scanned: string): boolean =>
    inbound != null && (registeredTrackings(inbound.doc).includes(scanned) || inbound.doc.inboundNo === scanned);

  /**
   * The last-mile number of the parcel the desk is physically holding: the arrival being counted, or
   * the order on screen. It is collected at scan time rather than typed, which is the whole point of it.
   */
  const trackingOnScreen = (): string | null => inbound?.doc.tracking ?? order?.tracking?.[0]?.number ?? null;

  /** Parks the parcel the catalog would not place, which is the only way out of the dead end [F Flow 2b]. */
  /** Failures propagate: an empty list here would read as "no such order" and send a valid parcel to the pool. */
  const lookupPurchaseOrder = async (orderNo: string): Promise<PurchaseOrderCandidate[]> => {
    try {
      return (await client.lookupPurchaseOrder(orderNo)).candidates;
    } catch (err) {
      reportFailure(err);
      throw err;
    }
  };

  /** Registers the dead-end code onto the matched line, which is what makes the rescan resolve [BR-27]. */
  const matchScannedTracking = async (code: string, candidate: PurchaseOrderCandidate) => {
    let poolMatched = 0;
    const done = await runInboundAction(
      async () => {
        const saved = await client.updateLineAgentTracking(candidate.orderId, candidate.lineNo, {
          lineTrackingNumber: code,
        });
        // A parcel parked earlier under this code is the same box; the server closed it on this line (SSD-7442).
        poolMatched = saved.unrecognizedMatched?.rows ?? 0;
        return saved;
      },
      {
        text: `\u2713 Tracking No ${code} matched and registered`,
        note: `Order ${candidate.orderId} — rescanning the same barcode is now recognized`,
      },
    );
    if (done && poolMatched > 0) {
      showSuccess(
        `Also closed ${poolMatched} unrecognized parcel row${poolMatched === 1 ? '' : 's'} parked under ${code}`,
      );
    }
    if (done) {
      setLookupCode(null);
    }
  };

  const registerUnrecognized = async (request: RegisterUnrecognizedScanRequest) => {
    setSendToPool(null);
    setPoolRefusal(null);
    beginAction();
    try {
      const registered = await client.registerUnrecognizedScan(request);
      // The server recognises the same parcel and hands back the row it already holds; reporting that
      // as a fresh registration would have the desk believe it had parked two parcels [T11].
      setToast(
        registered.reused
          ? { text: '✓ Already in the pool', note: 'This parcel was registered before — carry on scanning' }
          : { text: '✓ Sent to the unrecognized pool', note: 'Someone matches it there — carry on scanning' },
      );
      // The dead-end banner is answered, so the desk is not left looking at a failure it already handled.
      setBanner(null);
    } catch (err) {
      // The dialog has already closed, so the refusal stays on screen beside the dead end it answers
      // rather than in a toast the next one can push away. The pool exists for parcels the catalog
      // cannot place, so that refusal is the start of the flow: the desk is offered the parking anyway.
      const refusal = failureOf(err);
      const status = refusal?.response?.status;
      if (status === 400 && refusal?.response?.data?.code === UNKNOWN_BARCODE) {
        setPoolRefusal({
          request,
          reason: plainRefusalOf(serverRefusal(err), UNPLACEABLE_FALLBACK),
          guidance: UNPLACEABLE_GUIDANCE,
          parkable: true,
        });
      } else {
        setPoolRefusal({
          request,
          reason: serverRefusal(err) ?? GENERIC_ERROR,
          guidance: status === 409 ? POOL_ENTRY_CLOSED_GUIDANCE : POOL_SEND_FAILED_GUIDANCE,
          parkable: false,
        });
      }
      FulfillmentSound.failure();
    } finally {
      endAction();
      refocusSearch();
    }
  };

  /** A bare order number is the one code the scan dispatcher cannot resolve; the desk still expects it to open. */
  const openScannedOrderNumber = async (scanned: string): Promise<boolean> => {
    if (!/^\d+$/.test(scanned)) {
      return false;
    }
    try {
      const orderId = Number(scanned);
      await loadOrder(orderId);
      // An order number names the order, not one of its parcels.
      setSearchedTracking(null);
      followMatchedRun({ orderId });
      clearInbound();
      setBanner(null);
      return true;
    } catch {
      // Not an order either — the caller falls through to the dead-end banner and the lookup it opens.
      return false;
    }
  };

  /** Dispatch and runner-up speak different vocabularies, translated here rather than in the render [T07]. */
  const candidatesOf = (result: ScanCodeResponse): ScanCandidate[] => [
    {
      kind: result.kind as ScanCandidate['kind'],
      ...(result.orderId != null && { orderId: result.orderId }),
      ...(result.trackingNumber != null && { trackingNumber: result.trackingNumber }),
      ...(result.docId != null && { docId: result.docId }),
      ...(result.inboundNo != null && { inboundNo: result.inboundNo }),
      ...(result.sku != null && { sku: result.sku }),
    },
    ...(result.alsoMatched ?? []).map(candidateOfMatch),
  ];

  /** A runner-up in the dispatch vocabulary the rest of this screen speaks. */
  const candidateOfMatch = (match: ScanCodeMatch): ScanCandidate => ({
    // A line's waybill still opens the order that line belongs to, so both read as an order here.
    kind: match.kind === 'INBOUND_REQUEST' ? 'INBOUND_REQUEST' : 'ORDER_WAYBILL',
    ...(match.orderId != null && { orderId: match.orderId }),
    ...(match.trackingNumber != null && { trackingNumber: match.trackingNumber }),
    ...(match.docId != null && { docId: match.docId }),
    ...(match.inboundNo != null && { inboundNo: match.inboundNo }),
    ...(match.sku != null && { sku: match.sku }),
  });

  /** How one candidate reads on its button; the desk picks by what it can see on the parcel. */
  const describeCandidate = (candidate: ScanCandidate): string => {
    if (candidate.kind === 'INBOUND_REQUEST') {
      return `Inbound request ${candidate.inboundNo ?? candidate.docId ?? ''}`;
    }
    if (candidate.kind === 'RETURN_WAYBILL') {
      return `Return of order ${candidate.orderId ?? ''}`;
    }
    if (candidate.kind === 'PRODUCT_BARCODE') {
      return `Product ${candidate.sku ?? ''}`;
    }
    return `Order ${candidate.orderId ?? ''}`;
  };

  /** Takes a candidate down the path that candidate alone implies. */
  const openCandidate = async (candidate: ScanCandidate, code?: string, read?: FulfillmentOrderDetail) => {
    // Held scans wait for the chosen parcel to be on screen, not just for the question to go away.
    setScanning(true);
    try {
      if (candidate.kind === 'INBOUND_REQUEST' && candidate.docId) {
        await openInbound(candidate.docId, candidate.inboundNo);
        return;
      }
      if (candidate.orderId != null) {
        if (candidate.kind === 'RETURN_WAYBILL') {
          setBanner({
            kind: 'RETURN_WAYBILL',
            orderId: candidate.orderId,
            ...(candidate.trackingNumber && { trackingNumber: candidate.trackingNumber }),
          });
        } else {
          setBanner(null);
        }
        clearInbound();
        await loadOrder(candidate.orderId, read);
        setSearchedTracking(code ?? candidate.trackingNumber ?? null);
      }
    } finally {
      setScanning(false);
    }
  };

  /**
   * The listed orders already outbounded or called off. Read before the parcel in hand opens, so the order on
   * screen stays the last one read; one that cannot be read is kept listed.
   */
  const closedOrdersOf = async (candidates: ScanCandidate[]): Promise<Set<number>> => {
    const orderIds = candidates.flatMap((candidate) =>
      candidate.kind === 'ORDER_WAYBILL' && candidate.orderId != null ? [candidate.orderId] : [],
    );
    const details = await Promise.all(
      orderIds.map((orderId) => client.getOrderDetailForOutbound(orderId).catch(() => null)),
    );
    return new Set(details.flatMap((detail) => (detail && isClosedOrder(detail) ? [detail.header.orderId] : [])));
  };

  /** A listed order that is already done is counted done, so the run never moves on to it by itself [SSD-7400]. */
  const markClosedOrders = async (closed: Promise<Set<number>>) => {
    const ids = await closed;
    const run = matchedRunRef.current;
    if (!run || ids.size === 0) {
      return;
    }
    const newlyDone = run.candidates.filter(
      (other) =>
        other.kind === 'ORDER_WAYBILL' && other.orderId != null && ids.has(other.orderId) && !run.done.includes(other),
    );
    if (newlyDone.length > 0) {
      commitMatchedRun({ ...run, done: [...run.done, ...newlyDone] });
    }
  };

  /**
   * The desk names the parcel in hand. Every parcel the waybill named stays offered, so the desk can go back
   * to any of them, until it scans something else or says neither [SSD-7416].
   */
  const chooseCandidate = async (candidate: ScanCandidate) => {
    const run = matchedRunRef.current;
    if (!run) {
      return;
    }
    // Read once, on the first pick, before the parcel in hand opens, so the order on screen stays the last one read.
    const closed = run.current == null ? closedOrdersOf(run.candidates) : null;
    commitMatchedRun({ ...run, current: candidate });
    await openCandidate(candidate, run.code);
    if (closed) {
      await markClosedOrders(closed);
    }
  };

  /**
   * A waybill that names several orders opens one of them at once, under the list, so the order the last scan
   * opened never stays on screen as if it were this waybill's (SSD-7389). It is the first one, in the server's
   * order, that could be read and is not outbounded or called off; the desk still names the parcel in hand from
   * the list, which stays [SSD-7416]. Each order is read once, and the one opened is not read again.
   */
  const openMatchedOrders = async (code: string, candidates: ScanCandidate[]) => {
    const reads = await Promise.allSettled(
      candidates.map((candidate) => client.getOrderDetailForOutbound(candidate.orderId as number)),
    );
    const readOf = (index: number) => {
      const outcome = reads[index];
      return outcome?.status === 'fulfilled' ? outcome.value : null;
    };
    const closed = candidates.filter((_, index) => {
      const detail = readOf(index);
      return detail !== null && isClosedOrder(detail);
    });
    const openIndex = candidates.findIndex((candidate, index) => readOf(index) !== null && !closed.includes(candidate));
    const index = openIndex >= 0 ? openIndex : candidates.findIndex((_, at) => readOf(at) !== null);
    const first = candidates[index];
    const read = readOf(index);
    commitMatchedRun({ code, candidates, current: first ?? null, done: closed });
    if (!first || !read) {
      // None of them could be read: the order from the last scan must not stand in for them.
      clearOrder();
      const failed = reads.find((outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected');
      throw failed?.reason ?? new Error(`No order on ${code} could be opened`);
    }
    await openCandidate(first, code, read);
  };

  /** Marks the run's own parcel finished, so the run never brings it up again by itself. */
  const finishCurrent = (run: MatchedRun): MatchedRun =>
    run.current && !run.done.includes(run.current) ? { ...run, done: [...run.done, run.current] } : run;

  /**
   * A parcel opened some other way than the list (its own code, its order number, its Expected Inbound row) is
   * one the run has to follow. One the waybill named becomes the one on screen, as if picked from the list;
   * anything else means the desk moved on, so the run ends [SSD-7394].
   */
  const followMatchedRun = (opened: ProcessedParcel) => {
    const run = matchedRunRef.current;
    if (!run) {
      return;
    }
    const claimed = run.candidates.find((other) => isParcel(other, opened));
    if (!claimed) {
      // Kept, the old list would ride above an order its waybill never named.
      commitMatchedRun(null);
      return;
    }
    if (claimed !== run.current) {
      commitMatchedRun({ ...run, current: claimed });
    }
  };

  /**
   * Once the run's own parcel is done, the next one the waybill named that is not done yet comes up by itself,
   * so the desk never has to remember it. Anything else finished on this screen leaves the run where it is.
   */
  const continueMatched = async (processed: ProcessedParcel) => {
    const run = matchedRunRef.current;
    if (!run?.current || !isParcel(run.current, processed)) {
      return;
    }
    const finished = finishCurrent(run);
    commitMatchedRun(finished);
    const at = finished.candidates.indexOf(run.current);
    const next = [...finished.candidates.slice(at + 1), ...finished.candidates.slice(0, at)].find(
      (other) => !finished.done.includes(other),
    );
    if (!next) {
      return;
    }
    try {
      await chooseCandidate(next);
    } catch (err) {
      reportFailure(err);
    }
  };

  /**
   * An outbounded parcel is marked done without the next one coming up, so the order just shipped stays on
   * screen [SSD-7390]; the desk opens the next from the list or by scanning it.
   */
  const releaseMatched = (processed: ProcessedParcel) => {
    const run = matchedRunRef.current;
    if (run?.current && isParcel(run.current, processed)) {
      commitMatchedRun(finishCurrent(run));
    }
  };

  /**
   * Closes the parked rows on the lines of the order the scan opened (SSD-7442): each resolves onto its line as a desk
   * match, so the pool forgets the box and the FC team's thread says where it went. One refusal leaves the others closed.
   */
  const closeParkedParcel = async (orderId: number, rows: ScanPoolParcelRow[]) => {
    beginAction();
    let closed = 0;
    try {
      for (const row of rows) {
        if (row.lineNo === null || !row.lineSku) {
          continue;
        }
        await client.resolveUnrecognizedScan(row.id, {
          sku: row.lineSku,
          orderId,
          lineNo: row.lineNo,
          via: 'DESK_SCAN',
        });
        closed += 1;
        // A closed row leaves the notice at once, so a refusal further on leaves only the rows still open to retry.
        setParkedHere((current) =>
          current && current.orderId === orderId
            ? {
                ...current,
                parcel: { ...current.parcel, rows: current.parcel.rows.filter((open) => open.id !== row.id) },
              }
            : current,
        );
      }
      setParkedHere(null);
      showSuccess(
        `✓ Closed in the unrecognized pool — ${closed} product${closed === 1 ? '' : 's'} on Order ${orderId}`,
      );
    } catch (err) {
      showFailure(
        err,
        closed > 0 ? `Closed ${closed}, then the pool refused the next one` : 'The pool did not close this box',
      );
    } finally {
      endAction();
    }
  };

  const applyScan = (result: ScanCodeResponse, scanned: string): number | null => {
    if (result.kind === 'INBOUND_REQUEST') {
      clearOrder();
      setBanner({ kind: 'INBOUND_REQUEST', docId: result.docId, inboundNo: result.inboundNo });
      setInboundDone(null);
      return null;
    }
    if (result.kind === 'PRODUCT_BARCODE') {
      const match = result.sku ? findScannedLine(result.sku) : undefined;
      setBanner({
        kind: 'PRODUCT_BARCODE',
        ...(match && order && { orderId: order.header.orderId }),
        commentReadId: ++commentReadSequence.current,
        sku: result.sku,
        productName: result.productName,
        packSize: result.packSize,
        match,
      });
      return null;
    }
    if (result.kind === 'RETURN_WAYBILL' && result.orderId != null) {
      setBanner({ kind: 'RETURN_WAYBILL', orderId: result.orderId, trackingNumber: result.trackingNumber ?? scanned });
      return result.orderId;
    }
    if (result.kind === 'UNKNOWN' || result.orderId == null) {
      setBanner({
        kind: 'UNKNOWN',
        code: scanned,
        ...(result.parkedScanId !== undefined && { parkedScanId: result.parkedScanId }),
      });
      // The dead end itself opens the lookup — the desk should not have to ask for it [L-S1-Fb] [E-1].
      setLookupCode(scanned);
      return null;
    }
    setBanner(null);
    return result.orderId;
  };

  /** Logs the code on the desk's feed and queues it, the same for a typed code and one linked in. */
  const queueSearch = (scanned: string) => {
    const now = new Date();
    const today = feedDayOf(now);
    setFeed((previous) => ({
      day: today,
      scans: [
        { code: scanned, at: clockTime(now), worker: 'This desk' },
        // A feed carried over from yesterday is yesterday's; the day's first scan starts it again.
        ...(previous.day === today ? previous.scans : []),
      ].slice(0, ON_SCREEN),
    }));
    setScanQueue((previous) => [...previous, scanned]);
  };

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const scanned = scannedCode(code);
    if (!scanned) {
      return;
    }
    // The code stays in the box, selected, so the next scan replaces it [SSD-7135]: a second barcode
    // appended to the first is a code neither desk nor server can read [T07], and emptying the box hid
    // what was scanned. A repeat of the untouched code straight after is the scanner's own second line
    // terminator (or a double click on Search), not a second parcel, and is not sent twice.
    const now = Date.now();
    const last = lastSubmit.current;
    if (last && last.code === scanned && !typedSinceSubmit.current && now - last.at < SCAN_SETTLED_MS) {
      searchInput.settle();
      return;
    }
    lastSubmit.current = { code: scanned, at: now };
    typedSinceSubmit.current = false;
    searchInput.settle();
    queueSearch(scanned);
  };

  // Order Detail links here with its order number [SSD-7137]. Each navigation that carries one is searched
  // once, then the code is dropped from the URL so a reload or Back does not search it a second time.
  const linkedSearchKey = useRef<string | null>(null);
  useEffect(() => {
    if (linkedSearchKey.current === locationKey) {
      return;
    }
    linkedSearchKey.current = locationKey;
    const linked = scannedCode(searchParams.get(VIEW_ORDERS_SEARCH_PARAM) ?? '');
    if (!linked) {
      return;
    }
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(VIEW_ORDERS_SEARCH_PARAM);
        return next;
      },
      { replace: true },
    );
    queueSearch(linked);
  });

  /** A closed request leaves the address too, so a reload or Back does not open it again [SSD-7226 D4]. */
  const forgetView = () => {
    if (!VIEW_PARAMS.some((name) => searchParams.has(name))) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    VIEW_PARAMS.forEach((name) => next.delete(name));
    next.delete(VIEW_ORDERS_SEARCH_PARAM);
    writtenView.current = null;
    selfWritten.current = next.toString();
    setSearchParams(next, { replace: true });
  };

  // The address follows the screen [SSD-7226 D4]: the first order or request opened replaces the entry, and
  // each one after it is a new entry, so Back walks the desk through what it opened. A Back or Forward that
  // brought the address here is not answered by writing it back; the screen follows the address below.
  const shownOrderId = order?.header.orderId ?? null;
  const shownInboundId = order ? null : (inbound?.doc.docId ?? null);
  const shownReturn =
    banner?.kind === 'RETURN_WAYBILL' && banner.orderId === shownOrderId ? (banner.trackingNumber ?? '') : null;
  const writtenView = useRef<string | null>(null);
  /** The address this screen wrote last, so the navigation it caused is not mistaken for a Back or Forward. */
  const selfWritten = useRef<string | null>(null);
  useEffect(() => {
    const wanted: Array<[string, string]> =
      shownOrderId !== null
        ? [
            [VIEW_ORDERS_ORDER_PARAM, String(shownOrderId)],
            ...(shownReturn !== null ? ([[VIEW_ORDERS_RETURN_PARAM, shownReturn]] as Array<[string, string]>) : []),
          ]
        : shownInboundId !== null
          ? [[VIEW_ORDERS_INBOUND_PARAM, shownInboundId]]
          : [];
    const view = wanted.map((pair) => pair.join('=')).join('&');
    if (!view || writtenView.current === view) {
      return;
    }
    writtenView.current = view;
    // A linked code is searched once; an address read before its removal landed must not carry it back.
    const next = new URLSearchParams(searchParams);
    VIEW_PARAMS.forEach((name) => next.delete(name));
    next.delete(VIEW_ORDERS_SEARCH_PARAM);
    wanted.forEach(([name, value]) => next.set(name, value));
    if (next.toString() === searchParams.toString()) {
      return;
    }
    const hadView = VIEW_PARAMS.some((name) => searchParams.has(name));
    selfWritten.current = next.toString();
    setSearchParams(next, { replace: !hadView });
  }, [shownOrderId, shownInboundId, shownReturn, searchParams, setSearchParams]);

  // The screen follows the address: a reload, Back or Forward that names another order or request reopens it,
  // in the queue's turn so nothing else reads the screen meanwhile. A linked search is the code above's to run.
  const reopenedKey = useRef<string | null>(null);
  const inboundDocId = inbound?.doc.docId ?? null;
  useEffect(() => {
    if (reopenedKey.current === locationKey) {
      return;
    }
    reopenedKey.current = locationKey;
    // The screen's own write lands here once; only an address the desk navigated to reopens anything.
    if (selfWritten.current !== null && searchParams.toString() === selfWritten.current) {
      selfWritten.current = null;
      return;
    }
    const view = viewInAddress(searchParams);
    const returnParam = searchParams.get(VIEW_ORDERS_RETURN_PARAM);
    if (view && 'order' in view) {
      if (shownOrder.current?.header.orderId !== view.order || (returnParam ?? null) !== shownReturn) {
        setPendingView(view);
        queueReopen({ reopenOrder: view.order, ...(returnParam !== null && { returnTracking: returnParam }) });
      }
    } else if (view && view.inbound !== inboundDocId) {
      setPendingView(view);
      queueReopen({ reopenInbound: view.inbound });
    } else {
      setPendingView(null);
    }
  }, [locationKey, searchParams, inboundDocId, shownReturn]);

  // A request reopened from the address knows only its id until it is read; its number comes from the read.
  useEffect(() => {
    if (inbound && banner?.kind === 'INBOUND_REQUEST' && banner.docId === inbound.doc.docId && !banner.inboundNo) {
      const inboundNo = inbound.doc.inboundNo;
      if (inboundNo) {
        setBanner((previous) => (previous ? { ...previous, inboundNo } : previous));
      }
    }
  }, [inbound, banner]);

  /**
   * A product scanned while a shared waybill's run is showing is received into the oldest placed order of
   * that run still waiting for it, whichever parcel is open or whether one is picked yet [SSD-7413]. Every
   * order of the run is read afresh, so the next unit of the same SKU goes to the next-oldest order. The
   * receipt goes through the shared action, so the shelf, sound, toast and Cancel Inbound stay as they are.
   */
  const inboundIntoRun = async (run: MatchedRun, product: ScanCodeResponse, sku: string, retried = false) => {
    const runOrders = run.candidates.filter(
      (candidate): candidate is ScanCandidate & { orderId: number } =>
        candidate.kind === 'ORDER_WAYBILL' && candidate.orderId != null,
    );
    const details = await Promise.all(
      runOrders.map((candidate) => client.getOrderDetailForOutbound(candidate.orderId).catch(() => null)),
    );
    // An order that could not be read may be the oldest one waiting, so the unit is not given to a younger one.
    const unread = runOrders.filter((_, at) => details[at] === null).map((candidate) => candidate.orderId);
    if (unread.length > 0) {
      showWarning(`Order ${unread.join(', ')} on ${run.code} could not be read — nothing was inbounded`, [
        'Scan the product again',
      ]);
      FulfillmentSound.failure();
      return;
    }
    const readable = details.filter((detail): detail is FulfillmentOrderDetail => detail !== null);
    const waiting = oldestWaitingLine(readable, sku);
    const scannedProduct: ScanBanner = {
      kind: 'PRODUCT_BARCODE',
      sku,
      productName: product.productName,
      packSize: product.packSize,
      runCode: run.code,
      commentReadId: ++commentReadSequence.current,
    };
    if (!waiting) {
      // [BR-26] a unit already in is refused as it is on a single order: named on the banner, never received twice.
      const arrived = arrivedLine(readable, sku);
      setBanner({
        ...scannedProduct,
        ...(arrived && {
          orderId: arrived.detail.header.orderId,
          match: { lineNo: arrived.line.lineNo, arrived: true },
        }),
      });
      if (!arrived) {
        showWarning(`No order on ${run.code} is waiting for ${sku} — nothing was inbounded`);
        FulfillmentSound.failure();
      }
      return;
    }
    const orderId = waiting.detail.header.orderId;
    const candidate = runOrders.find((other) => other.orderId === orderId) ?? null;
    // The scan names the parcel the way a pick from the list does, so a first one also counts the run's
    // already-finished orders done, and the run never moves on to them by itself [SSD-7400].
    const closed =
      run.current == null
        ? runOrders.filter(
            (other) =>
              !run.done.includes(other) &&
              readable.some((detail) => detail.header.orderId === other.orderId && isClosedOrder(detail)),
          )
        : [];
    commitMatchedRun({ ...run, current: candidate, done: [...run.done, ...closed] });
    clearInbound();
    setBanner({ ...scannedProduct, orderId, match: { lineNo: waiting.line.lineNo, arrived: false } });
    const detail = await loadOrder(orderId);
    setSearchedTracking(run.code);
    const line = detail.lines.find((other) => other.lineNo === waiting.line.lineNo);
    if (!line || !canInboundLine(line.lineStatus) || isCancelledOrder(detail.header)) {
      // Another desk got there between the two reads; the run is read once more so the unit goes to the next order.
      if (!retried) {
        await inboundIntoRun(matchedRunRef.current ?? run, product, sku, true);
      } else {
        showWarning(`Order ${orderId} changed while it opened — nothing was inbounded`, ['Scan the product again']);
        FulfillmentSound.failure();
      }
      return;
    }
    if (!(await scannedQuantityMatches(line))) {
      return;
    }
    const locationCode = await inboundOne(orderId, line, false, refocusSearch, detail);
    if (locationCode) {
      setBanner((previous) => (previous ? { ...previous, receivedAt: locationCode } : previous));
      // A waybill several orders share leaves the outbound to the desk: the scan alone cannot say the parcel is whole.
      const stillWaiting = detail.lines.filter(
        (other) => other.lineNo !== line.lineNo && canInboundLine(other.lineStatus),
      );
      if (stillWaiting.length === 0) {
        showWarning(`Inbounded — order ${orderId} has every line in`, [
          'This waybill carries several orders, so outbound it with the Outbound button',
        ]);
      }
    }
  };

  /** One code at a time, in scan order, each read against the screen the one before it left. */
  const runScan = async (scanned: string) => {
    shelfAsked.current = false;
    setScanning(true);
    beginAction();
    setViolations([]);
    setFailures(NO_REFUSALS);
    // A new scan is a new parcel; the refused send and the dead end belonged to the one before it [SSD-7393].
    setPoolRefusal(null);
    setParkedHere(null);
    setBanner((previous) => (previous?.kind === 'UNKNOWN' ? null : previous));
    try {
      // A count in progress claims every scan but the request's own number and waybills; nothing about the
      // order on screen may move [L-S6-4]. The server's catalog decides what is a product, so a 12-digit code
      // is counted — and refused — like any other rather than guessed at as a waybill here [SSD-7204].
      // A product barcode while an open request is on screen counts into it. Anything the request does not
      // carry — another request, a customer waybill, an order number — is searched like any other scan and
      // opens what it names: the search box stays a search box while a request is open [SSD-7266].
      // A request confirmed but not read back is held as received: a product scan is not counted into it.
      const unreadConfirm = inboundRefreshStale === 'confirm';
      if (inbound && !isTrackingScan(scanned) && !inboundSettled && !unreadConfirm) {
        try {
          const counted = await client.countScanInbound(inbound.doc.docId, { barcode: scanned });
          setLastCount({ sku: counted.sku || scanned, at: clockTime(new Date()) });
          try {
            await loadInbound(inbound.doc.docId);
          } catch (refreshErr) {
            // The unit is on the server: a typed delta against the unread total would count it twice.
            if (inboundLocationsDocId.current === inbound.doc.docId) {
              setInboundRefreshStale('count');
            }
            throw refreshErr;
          }
          askWholeCheck();
          return;
        } catch (err) {
          if (!notOnRequest(err)) {
            throw err;
          }
        }
      }
      const dispatched = await client.scanCode({ code: scanned });
      const result = readScanCode(dispatched);
      // A product scanned while a received or cancelled request is open was not counted: the desk works by ear,
      // so it hears the failure and keeps the request rather than trading it for a product banner [SSD-6935].
      if (inbound && (inboundSettled || unreadConfirm) && result.kind === 'PRODUCT_BARCODE') {
        showWarning(
          !inboundSettled
            ? `Inbound No. ${inbound.doc.inboundNo ?? inbound.doc.docId} was confirmed but could not be read back — a scan is not counted; refresh the page to load it again`
            : `Inbound No. ${inbound.doc.inboundNo ?? inbound.doc.docId} is ${inboundStatusLabel(inbound.doc.status).toLowerCase()} — ${
                inboundReceived
                  ? 'a scan is not counted; type the Received qty to correct it'
                  : 'read-only, nothing was counted'
              }`,
        );
        FulfillmentSound.failure();
        return;
      }
      // The dispatcher places waybills and barcodes but never an order number, and the desk types those [E-1].
      if (result.kind === 'UNKNOWN' && (await openScannedOrderNumber(scanned))) {
        return;
      }
      // A product barcode nobody registered, scanned on an open order, is offered to that order's products [SSD-7677].
      // It comes after the order-number lookup, so an order number shaped like a barcode still opens its order.
      if (
        result.kind === 'UNKNOWN' &&
        order &&
        !inbound &&
        !orderCancelled &&
        !returnMode &&
        !matchedRunRef.current &&
        pendingInbound.length > 0 &&
        looksLikeProductBarcode(scanned)
      ) {
        if (await assignScannedBarcode(order, scanned)) {
          return;
        }
      }
      // Two parcels can carry one number, and only the person holding it knows which one this is [T07].
      if ((result.alsoMatched?.length ?? 0) > 0) {
        const candidates = candidatesOf(result);
        // The parcel in hand is the one just scanned, so a run still open, this waybill's own included, starts afresh [SSD-7400].
        if (candidates.every((candidate) => candidate.kind === 'ORDER_WAYBILL' && candidate.orderId != null)) {
          await openMatchedOrders(scanned, candidates);
        } else {
          // An inbound request or a return is another screen, so only the desk can say which one it holds [T07];
          // what the last scan opened goes, so it is never taken for one of them (SSD-7389).
          commitMatchedRun({ code: scanned, candidates, current: null, done: [] });
          clearOrder();
          clearInbound();
          setBanner(null);
        }
        return;
      }
      const run = matchedRunRef.current;
      if (
        result.kind === 'PRODUCT_BARCODE' &&
        result.sku &&
        run?.candidates.some((candidate) => candidate.kind === 'ORDER_WAYBILL' && candidate.orderId != null)
      ) {
        await inboundIntoRun(run, result, result.sku);
        return;
      }
      const orderId = applyScan(result, scanned);
      if (result.kind === 'INBOUND_REQUEST' && result.docId) {
        await loadInbound(result.docId);
        followMatchedRun({ docId: result.docId });
      }
      if (orderId != null) {
        clearInbound();
        const detail = await loadOrder(orderId);
        setSearchedTracking(scanned);
        // The same box may still be parked in the pool from before its waybill was registered (SSD-7442).
        const parked = dispatched.poolParcel;
        if (detail && parked && parked.rows.some((row) => row.orderId === orderId)) {
          setParkedHere({ orderId, parcel: parked });
        }
        followMatchedRun({ orderId });
        // A print failure never blocks or rolls back the receipt [E-39].
        // A returned parcel is never re-labelled, and the automatic path must be closed too [L-S4-1].
        // An outbound waybill read as its order is that order's label already in hand, so nothing prints [SSD-7409].
        // Printing on the scan is this workstation's choice, off unless the desk turned it on [SSD-7415].
        if (detail && autoPrintOnScan && dispatched.kind !== 'RETURN_WAYBILL' && autoPrintsOnScan(detail)) {
          autoPrint(detail);
        }
      }
      // [BR-26] scanning the product at the desk is what receives it, but only when the line is still
      // waiting. The receipt goes through the shared action so a refusal names its gate here too.
      if (result.kind === 'PRODUCT_BARCODE' && result.sku) {
        const waiting = lines.find((candidate) => candidate.sku === result.sku && canInboundLine(candidate.lineStatus));
        if (order && waiting && orderCancelled) {
          showWarning(`Order ${order.header.orderId} is cancelled — ${waiting.sku} was not inbounded`);
          FulfillmentSound.failure();
        } else if (order && waiting) {
          await receiveScannedLine(order, waiting);
        } else if (order) {
          // Not on this order, or already in: the banner names which, and the desk hears it without looking (SSD-7677).
          // An Existing Inventory line is the desk checking a shelf pick, so the right product hears the success chime (SSD-7990).
          const match = findScannedLine(result.sku);
          if (match?.fromShelf) {
            FulfillmentSound.success();
          } else if (match?.arrived) {
            FulfillmentSound.warning();
          } else {
            FulfillmentSound.failure();
          }
        }
      }
    } catch (err) {
      reportFailure(err);
    } finally {
      endAction();
      setScanning(false);
      // A receipt that left the order without a slot keeps the cursor in the Shelf; the desk scans that before scanning on.
      if (!shelfAsked.current) {
        refocusSearch();
      }
    }
  };

  /** A clicked Expected Inbound row, opened in the queue's turn so nothing else reads the screen meanwhile [15-3]. */
  const runOpenInbound = async ({ docId, inboundNo }: { docId: string; inboundNo?: string }) => {
    setScanning(true);
    beginAction();
    try {
      await openInbound(docId, inboundNo);
      followMatchedRun({ docId });
    } catch (err) {
      reportFailure(err);
    } finally {
      endAction();
      setScanning(false);
      refocusSearch();
    }
  };

  /** Reopens what the address names: the record is read again and nothing the search did is repeated [SSD-7226 D4]. */
  const runReopen = async (work: { reopenOrder: number; returnTracking?: string } | { reopenInbound: string }) => {
    setScanning(true);
    beginAction();
    try {
      if ('reopenOrder' in work) {
        clearInbound();
        // A returned parcel reopens in return mode, so its outbound and inbound actions stay held [L-S4-1] — only
        // on an outbound waybill the order itself carries, the same reading the scan gives a returned parcel: an
        // address is typed as easily as it is linked, and return mode frees a restock, so a supplier waybill or a
        // waybill off another order opens the order plainly and says so [SSD-7527].
        const detail = await client.getOrderDetailForOutbound(work.reopenOrder);
        const returnTracking = work.returnTracking;
        const carried =
          returnTracking !== undefined &&
          detail.tracking.some((shipment) => shipment.kind === 'OUTBOUND' && shipment.number === returnTracking);
        setBanner(
          carried && returnTracking !== undefined
            ? { kind: 'RETURN_WAYBILL', orderId: work.reopenOrder, trackingNumber: returnTracking }
            : null,
        );
        if (returnTracking !== undefined && !carried) {
          showWarning(
            `Waybill ${returnTracking} is not an outbound waybill of order ${work.reopenOrder} — opened without return mode`,
          );
        }
        await loadOrder(work.reopenOrder, detail);
        setCode(String(work.reopenOrder));
        followMatchedRun({ orderId: work.reopenOrder });
      } else {
        await openInbound(work.reopenInbound, undefined);
        followMatchedRun({ docId: work.reopenInbound });
      }
    } catch (err) {
      reportFailure(err);
    } finally {
      setPendingView(null);
      endAction();
      setScanning(false);
      refocusSearch();
    }
  };

  const queueReopen = (work: { reopenOrder: number; returnTracking?: string } | { reopenInbound: string }) =>
    setScanQueue((previous) => [...previous, work]);

  // The next scan must read the order, inbound and lines the previous one left on screen, so it is started
  // from an effect with this render's runScan, never from a closure the previous scan captured.
  const runQueued = (work: QueuedWork) =>
    typeof work === 'string'
      ? runScan(work)
      : 'openInbound' in work
        ? runOpenInbound(work.openInbound)
        : runReopen(work);
  const runQueuedRef = useRef(runQueued);
  runQueuedRef.current = runQueued;
  // An action that clears busy midway through a scan does not release the queue; only the scan finishing does.
  // A shared waybill's list does not hold it: a product barcode scanned behind it is received into the run's
  // oldest order still waiting for it [SSD-7413], and any other code is the desk moving on [SSD-7416]. An open
  // confirm holds it, so the order it names is still the one on screen when the desk answers.
  const awaitingDesk = lookupCode != null || confirmOpen;
  useEffect(() => {
    const next = scanQueue[0];
    if (busy || scanning || awaitingDesk || next === undefined) {
      return;
    }
    setScanQueue(scanQueue.slice(1));
    void runQueuedRef.current(next);
  }, [scanQueue, busy, scanning, awaitingDesk]);

  /**
   * A waybill or an order number read into the Shelf is a scan that landed in the wrong box, not a slot
   * [SSD-7226 D2]: nothing is saved, the Shelf goes back to the slot it held, and the code goes to the scan box
   * as if it had been scanned there — shown, selected and queued the same way, so nothing read is lost.
   */
  const handOffToSearch = (scanned: string) => {
    shelfEdit.current = null;
    setShelf(header?.temporaryLocation ?? '');
    setShelfDirty(false);
    setShelfSaved(false);
    // Shown and selected in the scan box, so the next read replaces it, and queued as a scan would be.
    const box = searchInput.ref.current;
    if (box) {
      box.value = scanned;
      box.focus({ preventScroll: true });
      box.select();
    }
    setCode(scanned);
    queueSearch(scanned);
  };

  const saveShelf = async () => {
    // A second Enter while the save is still out would send the slot twice, and the two answers could land
    // in either order; the first save is the one that counts.
    if (!header || shelfSaving) {
      return;
    }
    const orderId = header.orderId;
    const value = shelf.trim();
    // Inbound books the saved Shelf, so what is saved is the spelling the server will take [SSD-7817].
    const code = value ? readShelfCode(value) : '';
    // Only a shelf code is kept here; a scan read into the box is the scan box's [SSD-7226 D2].
    if (code === null) {
      // Left to the scan box a typed name would read as a failed search; a scan carries a run of digits and still goes.
      if (readsAsAName(value)) {
        showError(`${value} is not a shelf code. ${SHELF_CODE_RULE}`, undefined, AUTO_DISMISS);
        return;
      }
      const scanned = scannedCode(value);
      if (scanned) {
        handOffToSearch(scanned);
      }
      return;
    }
    const typing = shelfTyping.current;
    setShelfSaving(true);
    try {
      await client.updateTemporaryLocation(orderId, { value: code || null });
      // The loaded header carries the slot too, so an action's reload racing this save does not read it back old.
      setOrder((loaded) =>
        loaded && loaded.header.orderId === orderId
          ? { ...loaded, header: { ...loaded.header, temporaryLocation: code || null } }
          : loaded,
      );
      // Something typed over the box while the save was out is not what was saved, so it keeps its Save button.
      if (shelfTyping.current === typing && shownOrder.current?.header.orderId === orderId) {
        setShelf(code);
        setShelfDirty(false);
        setShelfSaved(true);
      }
      // Read when the desk leaves this order, so it must hold the slot just saved.
      if (shownOrder.current?.header.orderId === orderId) {
        shownOrder.current = {
          ...shownOrder.current,
          header: { ...shownOrder.current.header, temporaryLocation: code || null },
        };
      }
      if (value) {
        const asked = shelfAskToasts.current.get(orderId);
        if (asked !== undefined) {
          dismissToast(asked);
          shelfAskToasts.current.delete(orderId);
        }
      }
      // The rebin slot is recorded, so the next scan is the next parcel's waybill, not another slot.
      refocusSearch();
    } catch (err) {
      reportFailure(err);
    } finally {
      setShelfSaving(false);
    }
  };

  const bulkInbound = async () => {
    if (!header) {
      return;
    }
    if (orderCancelled) {
      showFailure(new Error('cancelled'), `Order ${header.orderId} is cancelled — its lines cannot be inbounded.`);
      return;
    }
    const targets = lines.filter((line) => selected.includes(line.lineNo) && canInboundLine(line.lineStatus));
    if (targets.length === 0) {
      showFailure(new Error('selection'), 'Select the lines to inbound first.', undefined, AUTO_DISMISS);
      return;
    }
    const planned = planLocations(targets);
    beginAction();
    setFailures(NO_REFUSALS);
    setSkippedLines([]);
    // From the click, so a slot scanned during the save lands in the Shelf [SSD-7226].
    focusShelf();
    const parked = document.activeElement;
    let landed = false;
    try {
      const { inbounded, refused, passedOver, unconfirmed } = await inboundByLocation(header.orderId, planned);
      landed = inbounded > 0;
      setFailures({ work: 'inbounded', lines: refused });
      setSkippedLines(passedOver);
      await loadOrder(header.orderId);
      setToast(bulkInboundToast(inbounded, planned.length, refused.length, passedOver.length, unconfirmed));
    } catch (err) {
      reportFailure(err);
    } finally {
      endAction();
      // Goods that came in go to the rebin station, so the cursor waits for the slot scan there [SSD-7052].
      settleCursor(landed, parked, focusShelf);
    }
  };

  const inboundAndOutboundAll = async () => {
    if (!header || carrierOutboundBlock || !beginOutboundWork({ orderId: header.orderId, kind: 'inbound-outbound' })) {
      return;
    }
    try {
      await inboundAndOutboundRemaining(header.orderId);
    } finally {
      endOutboundWork();
    }
  };

  const inboundAndOutboundRemaining = async (orderId: number) => {
    const planned = planLocations(pendingInbound);
    beginAction();
    setViolations([]);
    setFailures(NO_REFUSALS);
    setSkippedLines([]);
    // The order leaves, so the next scan is the next parcel: the cursor goes to the scan box from the click [SSD-7226].
    refocusSearch();
    // The desk stays busy from the first inbound to the outbound, so no queued scan runs between the two.
    let handedToOutbound = false;
    try {
      const { inbounded, refused, passedOver, unconfirmed } = await inboundByLocation(orderId, planned);
      setSkippedLines(passedOver);
      // A refused or unconfirmed line keeps the order from leaving, so the outbound waits until every line is in.
      if (refused.length > 0 || unconfirmed > 0) {
        setFailures({ work: 'inbounded', lines: refused });
        await loadOrder(orderId);
        setToast(bulkInboundToast(inbounded, planned.length, refused.length, passedOver.length, unconfirmed));
        FulfillmentSound.failure();
        return;
      }
      handedToOutbound = true;
    } catch (err) {
      reportFailure(err);
      return;
    } finally {
      if (!handedToOutbound) {
        endAction();
        refocusSearch();
      }
    }
    // busy counts actions in flight (#619), and the outbound counts its own, so the one begun above for the
    // inbound is let go here, exactly once and whatever the outbound does; left held, the desk stayed busy for
    // good and every later scan waited in the queue until a reload.
    let sent = false;
    try {
      sent = await runAction(
        orderId,
        () => client.outboundOrder(orderId),
        (detail) => outboundToast(detail, 'Inbound + Outbound'),
      );
    } finally {
      endAction();
    }
    if (sent) {
      soundOutbound();
      releaseMatched({ orderId });
    }
  };

  /**
   * The Shelf is left as it is [SSD-7226]: the desk presses Outbound first and then reads the slot off the
   * line to go and fetch the parcel, so clearing it on outbound (15-6) took away the number they needed.
   */
  const outbound = async () => {
    if (!header || carrierOutboundBlock) {
      return;
    }
    const orderId = header.orderId;
    if (!beginOutboundWork({ orderId, kind: 'outbound' })) {
      return;
    }
    let sent = false;
    try {
      sent = await runAction(
        orderId,
        () => client.outboundOrder(orderId),
        (detail) => outboundToast(detail, 'Outbound'),
      );
    } finally {
      endOutboundWork();
    }
    if (sent) {
      soundOutbound();
      releaseMatched({ orderId });
    }
  };

  /**
   * Undoing a shipment moves stock back and turns the shop's status back, so it asks first, as Order Detail
   * does: the scanner's button sat where the desk looked for the finish and was pressed as one [SSD-7390].
   */
  const rollbackOutbound = async () => {
    if (!header || cancelOutboundBlock !== '') {
      return;
    }
    const orderId = header.orderId;
    const agreed = await askConfirm({
      title: 'Cancel Outbound',
      message: `Order ${orderId} goes back to READY, its store status returns to processing, and its parcel stops counting as shipped.`,
      confirmLabel: 'Cancel outbound',
      cancelLabel: 'Keep the outbound',
    });
    if (!agreed || !beginOutboundWork({ orderId, kind: 'cancel' })) {
      return;
    }
    try {
      const cancelled = await runAction(
        orderId,
        () => client.cancelOutbound(orderId),
        (detail) => ({
          text: `✓ Outbound cancelled — Order ${orderId}`,
          note: `Order is ${detail.header.fulfillmentStatus} again · store status ${detail.header.wcStatus ?? 'unknown'}`,
        }),
      );
      // Back to READY, the order is not done any more: the shared-waybill list stops marking it so (SSD-7522).
      const run = matchedRunRef.current;
      if (cancelled && run && run.done.some((other) => isParcel(other, { orderId }))) {
        commitMatchedRun({ ...run, done: run.done.filter((other) => !isParcel(other, { orderId })) });
      }
    } finally {
      endOutboundWork();
    }
  };

  const confirmCancelInbound = async (request: CancelInboundLineRequest) => {
    if (!cancelTarget) {
      return;
    }
    const { orderId, lineNo, sku } = cancelTarget;
    setCancelTarget(null);
    await runAction(orderId, () => client.cancelInbound(orderId, lineNo, request), {
      text: `✓ Inbound cancelled — ${sku}`,
    });
  };

  /** Failures are collected per line: a restock the screen denies happened is how a line gets restocked twice. */
  const confirmReturnRestock = async (restockLines: ReturnRestockLine[], memo: string) => {
    if (!header) {
      return;
    }
    const orderId = header.orderId;
    setReturnRestockOpen(false);
    beginAction();
    setFailures(NO_REFUSALS);
    try {
      const settled = await Promise.allSettled(
        restockLines.map((row) =>
          putawayKeysRef.current.run(
            `${orderId}:${row.lineNo}:${row.locationCode}:${row.qty}:${memo.trim()}`,
            (idempotencyKey) =>
              client.returnPutaway(orderId, {
                idempotencyKey,
                lineNo: row.lineNo,
                locationCode: row.locationCode,
                qty: row.qty,
                ...(memo.trim().length > 0 && { memo: memo.trim() }),
              }),
          ),
        ),
      );
      const refused = settled.flatMap((outcome, index) =>
        outcome.status === 'rejected'
          ? [
              {
                lineNo: restockLines[index]?.lineNo ?? 0,
                reason: describeFailure(outcome.reason, GENERIC_ERROR),
              },
            ]
          : [],
      );
      setFailures({ work: 'restocked', lines: refused });
      await loadOrder(orderId);
      setToast(returnRestockToast(restockLines.length - refused.length, restockLines.length));
      if (refused.length === 0) {
        await continueMatched({ orderId });
      }
    } catch (err) {
      reportFailure(err);
    } finally {
      endAction();
      refocusSearch();
    }
  };

  const postComment = async (text: string, status?: OrderCommentStatus): Promise<boolean> => {
    if (!header) {
      return false;
    }
    beginAction();
    try {
      const said = JSON.stringify([text, status ?? null]);
      if (pendingCommentRef.current?.said !== said) {
        pendingCommentRef.current = { key: uuidv4(), said };
      }
      const idempotencyKey = pendingCommentRef.current.key;
      const response = await client.appendOrderComment(header.orderId, {
        text,
        idempotencyKey,
        ...(status && { status }),
      });
      pendingCommentRef.current = null;
      setComments(response.comments);
      return true;
    } catch (err) {
      reportFailure(err);
      return false;
    } finally {
      endAction();
    }
  };

  /** Pins one order note to the top of the thread, or takes the pin off; one pin per order (SSD-7609). */
  const pinComment = async (at: string, pinned: boolean) => {
    if (!header) {
      return;
    }
    beginAction();
    try {
      const response = await client.pinOrderComment(header.orderId, at, pinned);
      setComments(response.comments);
    } catch (err) {
      reportFailure(err);
    } finally {
      endAction();
      refocusSearch();
    }
  };

  /**
   * [L-S1-Fa] / [BR-14] as the plan's PB-7 reads (SSD-7389), first half: the scan that opens an order prints its label
   * when no bought product has been received into it yet, whatever its line count, since warehouse stock is not a
   * receipt. The second half is the product scan that receives the last bought line (lastToReceive). An order a scan
   * on this PC already printed does not print again.
   */
  const autoPrintsOnScan = (detail: FulfillmentOrderDetail): boolean => {
    if (autoPrintedBefore(detail.header.orderId)) {
      return false;
    }
    const bought = detail.lines.filter((line) => line.sourcingRoute !== EXISTING_INVENTORY);
    return (
      !detail.events.some((event) => event.operation === 'ADMIN_INBOUND') &&
      !bought.some((line) => isLineDone(line.lineStatus))
    );
  };

  /**
   * PB-7's second half: the product just scanned in was the only bought line still waiting, so the order is complete.
   * Warehouse stock scanned in is not a bought line. An order whose scan print went out before the carrier label
   * existed still prints here, for the label.
   */
  const lastToReceive = (detail: FulfillmentOrderDetail, received: FulfillmentOrderLine): boolean =>
    (!autoPrintedBefore(detail.header.orderId) || labelOwed(detail.header.orderId)) &&
    received.sourcingRoute !== EXISTING_INVENTORY &&
    detail.lines
      .filter((line) => line.sourcingRoute !== EXISTING_INVENTORY && canInboundLine(line.lineStatus))
      .every((line) => line.lineNo === received.lineNo);

  /**
   * The same paperwork the Print button sends, never the carrier payload itself [SSD-7415]. Not awaited: the scan,
   * Inbound and Outbound go on while it prints. Remembered as printed unless it fails, so a failed print is retried:
   * a printer that refuses the carrier label counts as failing, not only one that throws. A print with no carrier
   * label yet stays printed but owes the label to the last bought product scanned in.
   */
  const autoPrint = (detail: FulfillmentOrderDetail) => {
    const orderId = detail.header.orderId;
    rememberAutoPrint(orderId);
    void queuePrint(
      detail,
      () => {
        forgetAutoPrint(orderId);
        // Naming the print keeps a dead printer from reading as the receipt itself failing [E-39].
        showFailure(new Error('print'), 'Label did not print', ['The order is unaffected — print it again']);
      },
      (label) => {
        if (label === 'refused') {
          forgetAutoPrint(orderId);
        } else if (label === 'missing') {
          rememberLabelOwed(orderId);
        }
      },
    );
  };

  /** The parcel whose archived pdf is the carrier label, when it is not a drawn Deleo-family sticker. */
  const archivedLabelOf = (detail: FulfillmentOrderDetail) => {
    const parcel = currentOutboundShipmentOf(detail.tracking, detail.header.orderId, detail.pendingParcels);
    return parcel && !isPendingParcel(parcel) && !isDeleoParcel(parcel) && parcel.labelUrl ? parcel : undefined;
  };

  /**
   * The carrier's own label, exactly as the carrier issued it [G-4]: a Deleo-family sticker is drawn locally;
   * every other carrier's is the archived pdf. An order with no waybill yet has none to print.
   */
  const carrierLabelOf = async (detail: FulfillmentOrderDetail): Promise<CarrierLabel> => {
    const orderId = detail.header.orderId;
    const deleo = deleoTrackingOf(detail.tracking, orderId, detail.pendingParcels);
    const referencePending = deleoLabelRefusalOf(deleo);
    if (referencePending) {
      return { missing: referencePending };
    }
    const stickerNumber = deleoLabelNumberOf(deleo);
    if (stickerNumber) {
      return {
        job: {
          surface: 'carrierLabel',
          jobKey: `carrierLabel:${orderId}:${stickerNumber}`,
          role: 'label',
          format: 'dom',
          domPrint: () => printDeleoLabelThrough(orderId),
        },
      };
    }
    const issued = archivedLabelOf(detail);
    if (!issued) {
      return { missing: 'No label from the carrier yet' };
    }
    try {
      const bundle = await client.getLabelBundle([orderId]);
      if (bundle.skipped.includes(orderId)) {
        return { missing: 'The carrier label could not be read' };
      }
      return { pdf: bundle.pdf, trackingNumber: issued.number };
    } catch {
      return { missing: 'The carrier label could not be read' };
    }
  };

  /**
   * One toast for the click: what went to the printer, and the first sheet that did not and why. A sheet handed
   * to Chrome is a submitted job, not paper: where the print dialog shows, the desk can still cancel it there.
   */
  const reportOrderPrint = (outcomes: PrintOutcome[], carrier: CarrierLabel, sheetMissing?: string) => {
    const refused = outcomes.find((outcome): outcome is PrintFailure => !outcome.ok);
    if (refused) {
      setToast({
        text: PrintFailureNotice.textOf(refused),
        ...(sheetMissing && { note: sheetMissing }),
        failed: outcomes.some((outcome) => outcome.ok) ? 'partial' : true,
      });
      return;
    }
    if ('missing' in carrier) {
      const labelNote = `${carrier.missing} — no carrier label printed`;
      setToast(
        sheetMissing
          ? { text: 'Nothing printed', note: `${labelNote}. ${sheetMissing}`, failed: true }
          : { text: '\u2713 PACKING print job submitted', note: labelNote, failed: 'partial' },
      );
      return;
    }
    setToast(
      sheetMissing
        ? { text: '\u2713 Carrier label print job submitted', note: sheetMissing, failed: 'partial' }
        : { text: '\u2713 Carrier label and PACKING print job submitted' },
    );
  };

  /**
   * [L-F3] The order's dispatch paperwork, both on the label stock (shipping-label §1): the carrier label
   * for the parcel, then the PACKING invoice the packer checks the box against. A missing carrier label
   * never holds back the PACKING; the desk is told which one did not come out.
   */
  const printPaperwork = async (detail: FulfillmentOrderDetail): Promise<CarrierLabelPrint> => {
    const orderId = detail.header.orderId;
    // A PACKING read that fails never holds back the carrier label: the label prints and the sheet is named.
    const [read, invoice] = await Promise.all([
      carrierLabelOf(detail),
      client.getPackingInvoice(orderId).catch(() => null),
    ]);
    const sheetUnread = invoice ? undefined : PACKING_UNREAD;
    let carrier = read;
    if ('pdf' in carrier) {
      // The archived label and its PACKING go out as one pdf, one print, as on Order Detail [SSD-7207].
      let sent = false;
      const results = await printParcels(
        {
          dispatch: (job) => {
            sent = true;
            return printer.dispatch(job);
          },
        },
        [{ orderId, trackingNumber: carrier.trackingNumber, pdf: carrier.pdf, invoice }],
      );
      const { label } = results[0]!;
      if (sent || label.ok) {
        reportOrderPrint(sheetOutcomesOf(results), carrier, sheetUnread);
        return label.ok ? 'out' : 'refused';
      }
      carrier = { missing: label.reason };
    }
    const packing: { job: PrintJob | null; reason?: string | undefined } = invoice
      ? packingSheetJob(invoice)
      : { job: null, reason: PACKING_UNREAD };
    // The drawn label goes first, and the dispatcher waits for it before the PACKING is printed.
    const jobs = [...('job' in carrier ? [carrier.job] : []), ...(packing.job ? [packing.job] : [])];
    const outcomes = jobs.length > 0 ? await printer.dispatchAll(jobs) : [];
    reportOrderPrint(outcomes, carrier, packing.reason);
    // The drawn label is the first job, so the first outcome is the carrier label's.
    if (!('job' in carrier)) {
      return 'missing';
    }
    return outcomes[0]?.ok ? 'out' : 'refused';
  };

  /**
   * Prints an order's paperwork after any print still out, without holding the desk: only the Print button
   * waits on it. A failure is reported through `onFailure`, so each route words it its own way.
   */
  const queuePrint = (
    detail: FulfillmentOrderDetail,
    onFailure: (err: unknown) => void,
    onPrinted?: (label: CarrierLabelPrint) => void,
  ): Promise<void> => {
    printsOut.current += 1;
    setPrinting(true);
    const run = printChain.current
      .then(async () => {
        setPrintingOrder(detail);
        try {
          // Printed first, then reported: an optional call skips its arguments when nobody listens.
          const label = await printPaperwork(detail);
          // A carrier label out, from a scan or the Print button, settles any label a scan print still owed.
          if (label === 'out') {
            forgetLabelOwed(detail.header.orderId);
          }
          onPrinted?.(label);
        } catch (err) {
          onFailure(err);
        }
      })
      .finally(() => {
        printsOut.current -= 1;
        if (printsOut.current === 0) {
          setPrinting(false);
          setPrintingOrder(null);
        }
        // A print frame takes the cursor while it prints; a field the desk moved to meanwhile keeps it.
        const cursor = document.activeElement;
        if (cursor === null || cursor === document.body || cursor instanceof HTMLIFrameElement) {
          refocusSearch();
        }
      });
    printChain.current = run;
    return run;
  };

  /** The Print button: the desk's own press, so a refusal is reported like any other action's. */
  const printOrder = () => {
    if (!order || printsOut.current > 0) {
      return;
    }
    // The disabled Print button drops the cursor to the page, where the next waybill read is lost, so the scan
    // box takes it from the click [SSD-7387].
    refocusSearch();
    void queuePrint(order, reportFailure);
  };

  /**
   * The label is its own document sized to the label stock: printed off the dialog in place, it took the
   * admin's page size and the dialog clipped it to the carrier name [SSD-6946]. The sheet stays up for a reprint.
   */
  const printReturnLabels = async (lineNos: number[], label: ReturnLabel) => {
    reportPrint(
      await printer.dispatch({
        surface: 'returnLabel',
        jobKey: `returnLabel:${header?.orderId ?? 'none'}:${lineNos.join(',')}`,
        role: 'label',
        format: 'html',
        html: ReturnLabelSheet.htmlOf(label),
      }),
      '\u2713 Return label print job submitted',
    );
  };

  const toggleLine = (lineNo: number) =>
    setSelected((previous) =>
      previous.includes(lineNo) ? previous.filter((no) => no !== lineNo) : [...previous, lineNo],
    );

  const bulkNote = (): string => {
    if (orderCancelled) {
      return 'Cancelled order — nothing on it can be inbounded';
    }
    if (returnMode) {
      return 'Opens the restock confirmation modal — confirm qty · location · memo per item, then process in bulk';
    }
    if (held) {
      return 'Hold order — Inbound allowed, Outbound blocked (ship after Hold release)';
    }
    if (selected.length > 0) {
      return `${selected.length} selected · Processing all triggers auto Outbound (Hold orders: Inbound only)`;
    }
    if (pendingInbound.length > 0) {
      return `${pendingInbound.length} not yet inbounded — processing all triggers auto Outbound (Hold orders: Inbound only)`;
    }
    return 'Nothing left to bulk-process — all items inbounded';
  };

  const productBannerNote = (shown: ScanBanner) => {
    // A receipt from a shared waybill's run names its order too, since the scan chose it rather than the desk.
    const where = (lineNo: number) =>
      shown.runCode && shown.orderId != null ? `order ${shown.orderId} line ${lineNo}` : `line ${lineNo}`;
    if (shown.receivedAt && shown.match) {
      return `Inbounded — ${shown.sku} · ${where(shown.match.lineNo)} is in at ${shown.receivedAt}.`;
    }
    if (!shown.match) {
      return shown.runCode
        ? `No order on ${shown.runCode} is waiting for this product — nothing was inbounded.`
        : order
          ? `Not on this order — ${shown.productName ?? shown.sku} (${shown.sku}). Check the product in hand; nothing was inbounded.`
          : 'The catalog knows this code, but no scanned order carries it. Scan the order waybill first.';
    }
    // An Existing Inventory line: this scan only checks the unit taken off the shelf and writes nothing (SSD-7990).
    if (shown.match.fromShelf) {
      return `Existing Inventory · right product — ${shown.sku} · ${where(shown.match.lineNo)}. Nothing was written.`;
    }
    // [BR-26] a barcode scanned twice says so rather than sending the operator back to the waybill.
    if (shown.match.arrived) {
      return `Already inbounded — ${shown.sku} · ${where(shown.match.lineNo)} is already in.`;
    }
    return `Line ${shown.match.lineNo} is waiting for this product — inbound that line below.`;
  };

  const selectedLines = lines.filter((line) => picked.has(line.lineNo));
  const stickerOrder = printingOrder ?? order;
  const stickerParcel = stickerOrder
    ? deleoTrackingOf(stickerOrder.tracking, stickerOrder.header.orderId, stickerOrder.pendingParcels)
    : null;

  /** The order card; names inside are the ones the card always used. */
  const renderOrderCard = (card: OrderCardView) => {
    const {
      live,
      detail,
      lines,
      displayLines,
      locations,
      logRows,
      comments,
      orderThread,
      shelf,
      shelfDirty,
      shelfSaved,
      selected,
      picked,
      orderCancelled,
      held,
      outbounded,
      shippable,
      pendingInbound,
      lastPending,
      bulkNote,
    } = card;
    const { header } = detail;
    const country = countryTagOf(header.countryCode);
    const shelfId = live ? 'view-orders-shelf' : `view-orders-shelf-${header.orderId}`;
    return (
      <>
        {orderCancelled && (
          <div className="failbanner" role="alert" aria-label="Cancelled order">
            <b>✕ Cancelled order — do not pack</b>
            <div>
              Order <b>{header.orderId}</b> is cancelled. Its lines cannot be inbounded; set the goods aside.
            </div>
            <CancelledGoodsNote events={detail.events} lines={detail.lines} />
          </div>
        )}

        {live && parkedHere?.orderId === header.orderId && (
          <PoolParcelNotice
            orderId={header.orderId}
            parcel={parkedHere.parcel}
            busy={busy}
            onClose={(rows) => void closeParkedParcel(header.orderId, rows)}
          />
        )}

        {held && (
          <div className="holdbanner" role="note" aria-label="Hold Shipment">
            <b>⏸ Hold Shipment</b>
            <span>
              {header.holdReason} — Order <b>{header.orderId}</b>
              {header.heldBy ? ` · Requested by ${header.heldBy}` : ''}
              {header.heldAt ? ` · ${logTimestamp(header.heldAt)}` : ''}
            </span>
          </div>
        )}

        <div className={header.isMarketing ? 'ordercard oc-marketing' : 'ordercard'} aria-busy={outboundWork !== null}>
          {/* One line of fixed cells, so a status or a button changing never moves the others (SSD-7389). */}
          <span className="kv oc-id">
            Order ID: <Link to={ROUTES.fulfillment.orderDetail(header.orderId)}>{header.orderId}</Link>
            {/* Marketing beside the number, as on the Orders list and Ready to Outbound (SSD-7512). */}
            {header.isMarketing && <span className="vo-mkt-badge">MKT</span>}
            {/* Where the parcel ships decides its carrier and paperwork, so the desk reads it beside the number [SSD-7423]. */}
            {country && (
              <span className="vo-country" title={country.name}>
                <span aria-hidden="true">{country.flag}</span> {country.code}
              </span>
            )}
          </span>
          {/* The shop's status lags the warehouse by its push, so the warehouse's own status sits beside it [SSD-7390]. */}
          {/* A marketing order has no shop order, so its store status would only ever read unknown (SSD-7512). */}
          <span className="oc-store">
            {header.isMarketing ? (
              <span className="kv">No shop order</span>
            ) : (
              <>
                <span className="kv">Store status:</span>
                <span className={`status ${storeStatusClass(header.wcStatus)}`}>{header.wcStatus ?? 'unknown'}</span>
              </>
            )}
          </span>
          <span className="oc-ff">
            <span className="kv">Fulfillment status:</span>
            <span
              className={`status ${wireframeStatusClass(header.fulfillmentStatus, header.holdReason)}`}
              title={header.holdReason ?? undefined}
            >
              {titleChipLabel(header.fulfillmentStatus, header.holdReason, detail.resolutionKind)}
            </span>
          </span>
          <span className="kv oc-qty">
            Total Quantity: <b>{header.totalQuantity}</b>
          </span>
          <span className="oc-shelf">
            <ShelfField
              id={shelfId}
              inputRef={live ? shelfRef : undefined}
              // Another card's Shelf takes typing once the press has made that card the one acted on (SSD-7389).
              readOnly={!live}
              value={shelf}
              dirty={shelfDirty}
              saved={shelfSaved}
              disabled={busy}
              onChange={(value) => {
                shelfEdit.current = header.orderId;
                shelfTyping.current += 1;
                setShelf(value);
                setShelfDirty(true);
                setShelfSaved(false);
              }}
              onSave={() => void saveShelf()}
            />
          </span>
          <span className="oc-act">
            {/* A returned order is never shipped from this screen, so it carries no outbound control [L-S4-1]. */}
            {returnMode ? null : outboundWork?.orderId === header.orderId ? (
              <span className="vo-working" role="status" aria-live="polite">
                <span className="wms-busy-ring" aria-hidden="true" />
                {outboundWorkLabel(outboundWork)}
              </span>
            ) : outbounded ? (
              // The Fulfillment status chip already says OUTBOUNDED, so the cell holds only the way back and the
              // order stays on one line (SSD-7389).
              <button
                type="button"
                className="btn btn-red-line btn-sm"
                disabled={busy || cancelOutboundBlock !== ''}
                title={cancelOutboundBlock || undefined}
                onClick={() => void rollbackOutbound()}
              >
                Cancel Outbound
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-outbound btn-sm"
                disabled={busy || held || !shippable || Boolean(carrierOutboundBlock)}
                title={carrierOutboundBlock || undefined}
                onClick={() => void outbound()}
              >
                Outbound
              </button>
            )}
            {!returnMode && !outbounded && carrierOutboundBlock && (
              <span className="blocked-note">{carrierOutboundBlock}</span>
            )}
          </span>
        </div>

        <div className="bulkbar">
          <button
            type="button"
            className="btn btn-green-line btn-sm"
            disabled={busy || linesLocked || pendingInbound.length === 0}
            onClick={() => void bulkInbound()}
          >
            Bulk Inbound (Selected)
          </button>
          {!returnMode && (
            <button
              type="button"
              className="btn btn-outbound btn-sm"
              disabled={busy || held || linesLocked || pendingInbound.length === 0 || Boolean(carrierOutboundBlock)}
              title={carrierOutboundBlock || undefined}
              onClick={() => void inboundAndOutboundAll()}
            >
              Inbound + Outbound All Remaining
            </button>
          )}
          {!returnMode && !outbounded && carrierOutboundBlock && (
            <span className="blocked-note">{carrierOutboundBlock}</span>
          )}
          {/*
            Return mode always offers it: detection is by scan, so the order may never have shipped [E-87]. An
            outbounded order opened any other way has its lines locked; its return is put away from Order Detail.
          */}
          {returnMode && (
            <button
              type="button"
              className="btn btn-green btn-sm"
              disabled={busy || selected.length === 0}
              onClick={() => setReturnRestockOpen(true)}
            >
              Restock Selected to Warehouse ({selected.length})
            </button>
          )}
          <span className="cnt">{bulkNote}</span>
          {/*
            Comments and Print close the bulk row, Print at the very end: it is pressed far more often, and from there
            the hand goes straight down to the lines' Inbound buttons in the right-hand Actions column (SSD-7389).
          */}
          <button
            type="button"
            className="btn btn-line btn-sm comment-btn"
            onClick={() => setCommentsOpen((was) => !was)}
          >
            <CommentIcon />
            Comments <span className="badge-n">{orderThread.length}</span>
          </button>
          {/* Where the order stands by its thread, beside its comments, so the desk sees it with the panel closed (SSD-7609). */}
          {threadStatus && (
            <CommentStatusChip status={threadStatus} title="The status of the latest note that set one" />
          )}
          {/* A returned order is never shipped or re-labelled from this screen [L-S4-1]. */}
          {!returnMode && (
            // Held by its own print and by a scan about to change the order on screen, never by Inbound or Outbound.
            <button
              type="button"
              className="btn btn-line btn-sm vo-bulk-print"
              aria-label="Print"
              disabled={printing || scanning}
              onClick={printOrder}
            >
              <PrintIcon />
              Print
            </button>
          )}
        </div>

        {commentsOpen && (
          // Keyed by order: a refused note waits for a retry on its own order and never opens on the next one (SSD-7609).
          <OperatorComments
            key={header?.orderId ?? 'none'}
            comments={orderThread}
            busy={busy}
            onPost={postComment}
            onPin={pinComment}
          />
        )}

        <div className={`table-wrap${linesLocked ? ' is-locked' : ''}`}>
          <table className="tbl tbl-lines" aria-label="Order lines" aria-disabled={linesLocked || undefined}>
            {/* Fixed columns, so a line's status or button never moves the rest (SSD-7389). */}
            <colgroup>
              <col className="c-check" />
              <col className="c-sku" />
              <col className="c-image" />
              <col />
              <col />
              <col className="c-size" />
              <col className="c-qty" />
              <col className="c-barcode" />
              <col className="c-inbound-no" />
              <col className="c-tracking" />
              <col className="c-status" />
              <col className="c-route" />
              <col className="c-location" />
              <col className="c-actions" />
            </colgroup>
            <thead>
              <tr>
                <CheckCell as="th" style={{ width: 34 }}>
                  <input
                    type="checkbox"
                    aria-label="Select every line"
                    disabled={linesLocked}
                    checked={selected.length > 0 && selected.length === lines.length}
                    onChange={(event) => setSelected(event.target.checked ? lines.map((line) => line.lineNo) : [])}
                  />
                </CheckCell>
                <th>SKU</th>
                <th>Image</th>
                <th>Product Name</th>
                <th>Product Name KR</th>
                <th>Size</th>
                <th>Qty</th>
                <th>Barcode</th>
                <th>Inbound Order No</th>
                <th>Tracking No</th>
                <th>Inbound Status</th>
                <th>Sourcing Route</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayLines.map((line) => {
                const product = catalog[line.sku];
                const brand = line.brand ?? product?.brand;
                const image = line.imageUrl ?? product?.productImageUrls?.[0];
                const rowClass = [
                  (scannedSku && line.sku === scannedSku) || matchesSearchedTracking(line, searchedTracking)
                    ? 'row-hit'
                    : '',
                  line.sourcingRoute === EXISTING_INVENTORY ? 'row-exist' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const stock = skuStock[(line.sku ?? '').trim()];
                const variation = variationPackDisplay(line);
                const row = (
                  <tr key={line.lineNo} className={rowClass}>
                    <CheckCell>
                      <input
                        type="checkbox"
                        aria-label={`Select ${line.sku}`}
                        disabled={linesLocked}
                        checked={picked.has(line.lineNo)}
                        onChange={() => toggleLine(line.lineNo)}
                      />
                    </CheckCell>
                    <td className="num">
                      <span className="vo-sku d-inline-flex align-items-center gap-1">
                        <StockHistoryLink sku={line.sku} onOpen={refocusSearch} />
                        {/* Copies what the link shows: the trimmed sku, and nothing for a blank one (SSD-7781). */}
                        {(line.sku ?? '').trim() && (
                          <CopyIcon value={(line.sku ?? '').trim()} label={`Copy SKU ${(line.sku ?? '').trim()}`} />
                        )}
                      </span>
                      {variation && (
                        <span className="d-block text-muted small" title="Ordered variation SKU">
                          ↳ {variation.orderedSku}
                        </span>
                      )}
                      {stock && (
                        <span
                          className="vo-stock"
                          data-testid="line-stock"
                          title="On hand: every unit of this SKU on the book, all shelves. Available: on hand less what orders already hold."
                        >
                          <span>{stock.onHand} on hand</span>
                          <span className={stock.available <= 0 ? 'is-short' : undefined}>
                            {stock.available} available
                          </span>
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="thumb">
                        {image ? (
                          <ProductImageZoom
                            src={image}
                            label={[brand, line.productName].filter(Boolean).join(' ') || line.sku}
                            className="is-fill"
                            onClosed={refocusSearch}
                          >
                            <img src={image} alt="" />
                          </ProductImageZoom>
                        ) : (
                          'IMG'
                        )}
                      </div>
                    </td>
                    <td>
                      {brand && <b>{brand}</b>} <span>{line.productName ?? MISSING}</span>
                      {/* The sales page shows the product's images; it opens in a new tab and the scan box keeps
        the cursor, so a scanner's Enter never reopens it [SSD-7392]. */}
                      {product?.productLink && (
                        <a
                          className="view-product"
                          href={product.productLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={refocusSearch}
                        >
                          View Product
                        </a>
                      )}
                    </td>
                    <td>
                      {/* The Korean name reads with the Korean brand when the mapper knows it (SSD-7389). */}
                      {brand && <b>{brandKr[brand] ?? brand}</b>} <span>{line.productNameKr ?? MISSING}</span>
                    </td>
                    <td>{line.size ?? product?.size ?? MISSING}</td>
                    <td>
                      <span className={`qty${line.totalQuantity === 1 ? '' : ' qty-warn'}`}>{line.totalQuantity}</span>
                      {variation?.math && (
                        <span
                          className="d-block text-muted small"
                          title="Ordered quantity × units per pack; Qty is total units"
                        >
                          {variation.math}
                        </span>
                      )}
                    </td>
                    <td>
                      <BarcodeCell
                        sku={line.sku}
                        barcode={product?.barcode}
                        productId={product?.productId}
                        onSave={(productId, code) => saveBarcode(productId, code, line.sku, product?.barcode)}
                        disabled={linesLocked}
                        editable
                        onEditClosed={refocusSearch}
                        productName={[brand, line.productName].filter(Boolean).join(' ') || undefined}
                        makeCode={{
                          code: internalBarcodeFor(line.sku),
                          onMake: (productId, code) =>
                            withScanCursor(() =>
                              makeBarcode(productId, code, line.sku, labelNamesOf(line, brand, product?.nameKor)),
                            ),
                          onPrint: (code) =>
                            withScanCursor(() =>
                              printCodeLabel(
                                code,
                                line.sku,
                                labelNamesOf(line, brand, product?.nameKor),
                                `✓ Label printed — ${code}`,
                              ),
                            ),
                        }}
                      />
                    </td>
                    <td className="num">{line.purchaseOrderNumber ?? MISSING}</td>
                    <td className="num track">{line.lineTrackingNumber ?? MISSING}</td>
                    <td>
                      <span className={`tag ${isLineDone(line.lineStatus) ? 'tag-inbounded' : 'tag-pending'}`}>
                        {line.lineStatus}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`tag tag-route${line.sourcingRoute === EXISTING_INVENTORY ? ' tag-route-exist' : ''}`}
                      >
                        {sourcingRouteLabel(line)}
                      </span>
                    </td>
                    <td className="num">
                      {/* Shows where existing stock sits (the stock's own route is kept on the line); a location is
        edited on Current Stocks, never here. A JIT line lives on the order's Shelf, so its cell
        is a dash [SSD-7226 D3]. */}
                      {line.sourcingRoute === 'JIT' ? (
                        <span className="loc-none">-</span>
                      ) : isV1WarehouseLocation(shownLocations.get(line.lineNo)) ? (
                        /* WH#00 is V1's bucket, not a shelf: a dash, as on labels, with the reason on hover. */
                        <span title="No shelf yet: V1 stock (WH#00)" aria-label="No shelf yet: V1 stock (WH#00)">
                          {MISSING}
                        </span>
                      ) : (
                        (shownLocations.get(line.lineNo) ?? MISSING)
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        {/* In return mode the row carries the return action alone [L-S4-1]. */}
                        {!returnMode && orderCancelled && canInboundLine(line.lineStatus) && (
                          <button
                            type="button"
                            className="btn btn-gray btn-sm"
                            disabled
                            title={`Order ${header.orderId} is cancelled — this line cannot be inbounded`}
                          >
                            Order cancelled
                          </button>
                        )}
                        {!returnMode && !orderCancelled && canInboundLine(line.lineStatus) && (
                          <button
                            type="button"
                            // Shipping the order too, it takes the outbound colour, never the green of a plain Inbound (SSD-7522).
                            className={`btn ${!held && !carrierOutboundBlock && lastPending?.lineNo === line.lineNo ? 'btn-outbound' : 'btn-green'} btn-sm`}
                            disabled={busy || linesLocked}
                            onClick={() => void inboundClicked(header.orderId, line)}
                          >
                            {!held && !carrierOutboundBlock && lastPending?.lineNo === line.lineNo
                              ? 'Inbound + Outbound'
                              : 'Inbound'}
                          </button>
                        )}
                        {/* A return goes back on the shelf through Restock Selected; cancelling the inbound is
        not that, whatever a per-row button in return mode said it was. */}
                        {!returnMode && (canCancelInbound(line.lineStatus, line.inboundReversible) || outbounded) && (
                          <button
                            type="button"
                            className="btn btn-red-line btn-sm"
                            // Reverting after outbound would leave the order in an order that never happened [BR-10].
                            disabled={busy || outbounded || !canCancelInbound(line.lineStatus, line.inboundReversible)}
                            onClick={() =>
                              setCancelTarget({
                                orderId: header.orderId,
                                lineNo: line.lineNo,
                                sku: line.sku,
                                arrivedQty: line.totalQuantity,
                                // Where the arrival is booked, the packing desk included: the server takes a
                                // write-off off the shelf named here and a restock moves nothing, so naming
                                // any other shelf would take stock off the wrong one [BR-58].
                                suggestedLocation: locations.get(line.lineNo) ?? shelf.trim(),
                                locationLocked: locations.has(line.lineNo),
                              })
                            }
                          >
                            Cancel Inbound
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                // Order comments live in the pane above only; a line's own memos sit under that line, whole,
                // so "oos" on line 2 is read where line 2 is scanned (SSD-7265).
                const memos = lineMemos(comments, line.lineNo);
                if (memos.length === 0) {
                  return row;
                }
                return (
                  <Fragment key={line.lineNo}>
                    {row}
                    <tr className="note-row" aria-label={`Line ${line.lineNo} memo`}>
                      <td colSpan={LINE_COLUMNS}>
                        <LineMemoThread comments={memos} />
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="foundrow">
          <p className="mut">Found {lines.length} item(s) in this order · Found 1 order(s)</p>
          {/* A returned parcel is restocked, never sent on to a supplier, so M4 stays off State 4 [L-S4-6]. */}
          {!returnMode && (
            <button
              type="button"
              className="btn btn-line btn-sm"
              disabled={linesLocked || selected.length === 0}
              onClick={() => setReturnLabelOpen(true)}
            >
              <PrintIcon />
              Print Return Labels ({selected.length})
            </button>
          )}
        </div>

        <div className="logsec">
          <h4>Inbound / Outbound Log</h4>
          <table className="logtbl" aria-label="Inbound / Outbound Log">
            <thead>
              <tr>
                <th>Time</th>
                <th>Action</th>
                <th>SKU</th>
                <th>Qty</th>
                <th>Worker</th>
                <th>Memo</th>
              </tr>
            </thead>
            <tbody>
              {logRows.map((row) => (
                <tr key={row.id}>
                  <td className="num">{row.at}</td>
                  <td className={`act-${row.tone}`}>{row.action}</td>
                  <td className="num">{row.sku}</td>
                  <td className="num">{row.qty}</td>
                  <td>{row.worker}</td>
                  <td>{row.memo}</td>
                </tr>
              ))}
              {detail.events.length === 0 && (
                <tr>
                  <td colSpan={6} className="mut">
                    Nothing has moved on this order yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <PackingRecordsLog events={detail.events} orderId={detail.header.orderId} />
      </>
    );
  };

  /**
   * The desk cannot tell which order a product in hand belongs to, so the list never asks; it says the waybill is on
   * several orders, shows them one at a time and lets the desk pick (SSD-7389). The next scan puts the list away.
   */
  const matchedTitle = matchedRun
    ? matchedRun.candidates.every((candidate) => candidate.kind === 'ORDER_WAYBILL')
      ? `${matchedRun.code} is on ${matchedRun.candidates.length} orders, shown one at a time — select an order`
      : `${matchedRun.code} matches ${matchedRun.candidates.length} results — select one`
    : '';

  /** The order the page's actions work on, as the screen holds it now. */
  const liveCard: OrderCardView | null = order
    ? {
        live: true,
        detail: order,
        lines,
        displayLines,
        locations,
        logRows,
        comments,
        orderThread,
        shelf,
        shelfDirty,
        shelfSaved,
        selected,
        picked,
        orderCancelled,
        held,
        outbounded,
        shippable,
        pendingInbound,
        lastPending,
        bulkNote: bulkNote(),
      }
    : null;

  return (
    <div className="wms-wireframe ff-ultimate wms-view-orders">
      {/* One feedback slot, the same component the other eight desk screens render [T03]. */}
      <FulfillmentToastHost toasts={toasts} onDismiss={dismissToast} />
      {order && (
        <PendingParcelNotice
          parcel={currentOutboundShipmentOf(order.tracking, order.header.orderId, order.pendingParcels)}
        />
      )}
      {/* The Deleo-family sticker is drawn off screen, so Print has one to hand the dialog: the printing order's, else the one on screen. */}
      {stickerOrder && !deleoLabelRefusalOf(stickerParcel) && deleoLabelNumberOf(stickerParcel) && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden' }} aria-hidden="true">
          <div ref={deleoLabelRef} data-order-id={stickerOrder.header.orderId}>
            <OrderPdfPreview
              order={toDeleoLabelOrder({
                orderId: stickerOrder.header.orderId,
                shipping: stickerOrder.header.shipping,
                lines: stickerOrder.lines,
                tracking: stickerOrder.tracking,
                pendingParcels: stickerOrder.pendingParcels ?? [],
                isMarketing: stickerOrder.header.isMarketing,
              })}
            />
          </div>
        </div>
      )}

      <div className="titlerow">
        <div>
          <h2>WMS - View Orders</h2>
          <p className="sub ff-label">Search Orders</p>
        </div>
        {/* Beside the title, so the setting is there before any scan and the order bar keeps its one row [SSD-7404]. */}
        <button
          type="button"
          className="btn btn-line btn-sm vo-settings-btn"
          aria-label="View Orders settings"
          title="Settings"
          onClick={() => setSettingsOpen(true)}
        >
          <GearIcon />
        </button>
      </div>

      <form className="searchrow" onSubmit={submitSearch}>
        <div className="search-input">
          <ScanIcon className="vo-scan-icon" />
          <CameraScanField label="Scan search code with camera">
            <input
              ref={searchInput.ref}
              aria-label="Search"
              {...asciiScanField((value) => {
                searchInput.noteTyping();
                typedSinceSubmit.current = true;
                setCode(value);
              })}
              // A camera read of the code still in the box changes no value, so onChange never runs; the
              // input event still fires, and a fresh read is a new scan, not the scanner's second terminator.
              onInput={() => {
                typedSinceSubmit.current = true;
              }}
              placeholder="Tracking No / Inbound Order No / Product Order No / Last-mile barcode — scan any number"
              value={code}
              onClick={searchInput.reclaim}
            />
          </CameraScanField>
        </div>
        {/* Never disabled: a disabled default button makes the browser swallow the scanner's Enter [8b]. */}
        <button type="submit" className="btn btn-blue">
          <SearchIcon />
          Search
        </button>
      </form>

      {matchedRun && (
        <div className="vo-ambiguous" role="alert" aria-label="Matched parcels">
          <div className="vo-ambiguous-head">
            <WarningIcon />
            <b>{matchedTitle}</b>
          </div>
          <div className="choices">
            {matchedRun.candidates.map((candidate) => {
              const open = candidate === matchedRun.current;
              // Outbounded, called off, or finished on this desk: it looks finished, so none of the waybill's parcels is missed (SSD-7522).
              const done = matchedRun.done.includes(candidate);
              return (
                <button
                  type="button"
                  // The order below is marked Showing, not checked: the screen opened it, the desk has not said it holds it (SSD-7389).
                  className={`btn btn-sm ${choosing ? 'btn-blue' : 'btn-line'}${open ? ' is-open' : ''}${done ? ' is-done' : ''}`}
                  key={`${candidate.kind}:${candidate.orderId ?? candidate.docId ?? candidate.sku ?? ''}`}
                  aria-pressed={choosing ? undefined : open}
                  // The Done pill stays out of the name; a screen reader still hears it as the button's description.
                  title={done ? 'Done — outbounded, called off, or finished on this desk' : undefined}
                  disabled={scanning || (!choosing && busy)}
                  onClick={() => void chooseCandidate(candidate).catch(reportFailure)}
                >
                  {describeCandidate(candidate)}
                  {done && (
                    <span className="vo-done" aria-hidden="true">
                      <CheckIcon />
                      Done
                    </span>
                  )}
                  {open && (
                    <span className="vo-showing" aria-hidden="true">
                      Showing
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!order && !banner && !choosing && pendingView && (
        <div className="waiting" role="status" aria-live="polite">
          <div className="icon">⏳</div>
          <div className="head">
            {'order' in pendingView ? `Opening order ${pendingView.order}…` : 'Opening the inbound request…'}
          </div>
        </div>
      )}

      {!order && !banner && !choosing && !pendingView && (
        <>
          <div className="waiting">
            <div className="icon">📡</div>
            <div className="head">Waiting for scan</div>
            <div className="mut">
              Scan a tracking/product barcode or type a number to <b>switch to that order&apos;s screen instantly</b>
            </div>
          </div>
          <ExpectedInboundPanel
            requests={expected}
            onOpen={(request) =>
              setScanQueue((previous) => [
                ...previous,
                { openInbound: { docId: request.docId, ...(request.inboundNo && { inboundNo: request.inboundNo }) } },
              ])
            }
          />
        </>
      )}

      {banner?.kind === 'INBOUND_REQUEST' && (
        <div className="intbanner" role="note" aria-label="Internal Inbound">
          <div className="big">
            <span>📦 Internal Inbound — Inbound Request</span>
            {/* The one way out that is not another request's code; it asks nothing, the count is already saved [SSD-7204]. */}
            <button
              type="button"
              className="btn btn-sm intclose"
              aria-label={`Close Inbound No. ${banner.inboundNo ?? banner.docId}`}
              disabled={busy}
              onClick={closeInbound}
            >
              ✕ Close
            </button>
          </div>
          <div className="warnline">Not a customer order · Goes into Inventory · No Outbound step</div>
          {/* The request's facts as chips, the way the wireframe's State 6 card reads them (SSD-7204). */}
          <div className="kv">
            <span className="chip">
              <span className="k">Inbound No.</span>
              <b>{banner.inboundNo ?? banner.docId}</b>
            </span>
            {/* Where the goods came from decides how they are counted and costed, so the desk sees it. */}
            {inbound?.doc.source && (
              <span className="chip">
                <span className="k">Route</span>
                <b className="tag tag-route">{describeInboundRoute(inbound.doc)}</b>
              </span>
            )}
            {inbound?.doc.supplier && (
              <span className="chip">
                <span className="k">Supplier</span>
                <b>{inbound.doc.supplier}</b>
              </span>
            )}
            {inbound && (
              <span className="chip">
                <span className="k">Status</span>
                <b
                  className={`tag ${
                    inbound.doc.status === INBOUND_STATUS.RECEIVED
                      ? 'tag-inbounded'
                      : inbound.doc.status === INBOUND_STATUS.CANCELLED
                        ? 'tag-cancelled'
                        : 'tag-pending'
                  }`}
                >
                  {inboundStatusLabel(inbound.doc.status)}
                </b>
              </span>
            )}
            {inbound && registeredTrackings(inbound.doc).length > 0 && (
              <span className="chip">
                <span className="k">Waybill</span>
                {/* The primary waybill is listed again among the registered ones, so it is shown once. */}
                <b>{[...new Set(registeredTrackings(inbound.doc))].join(' · ')}</b>
              </span>
            )}
          </div>
          {inbound && !inboundSettled && inbound.tallies.expected > 0 && (
            <div className="inbound-progress">
              <div
                className="bar"
                role="progressbar"
                aria-label="Counting progress"
                aria-valuemin={0}
                aria-valuemax={inbound.tallies.expected}
                aria-valuenow={Math.min(inbound.tallies.received, inbound.tallies.expected)}
              >
                <i
                  style={{
                    width: `${Math.min(100, Math.round((inbound.tallies.received / inbound.tallies.expected) * 100))}%`,
                  }}
                />
              </div>
              <div className="pct">
                {inbound.tallies.received} / {inbound.tallies.expected} received ·{' '}
                {Math.min(100, Math.round((inbound.tallies.received / inbound.tallies.expected) * 100))}%
              </div>
            </div>
          )}
          <div className="scanline">
            {inbound && inboundCancelled ? (
              <span>
                <b>Read-only</b> — this request is cancelled; nothing here can be received.
              </span>
            ) : inbound && inboundReceived ? (
              <span>
                <b>Received</b> — every unit is in stock. Lower a Received qty to take units back out (the request opens
                again), or raise the Expected qty (✎) if more arrives.
              </span>
            ) : (
              <span className="live">
                <i aria-hidden="true" />
                Scanning — each product barcode adds +1 · the cursor returns by itself
              </span>
            )}
            {/* The toast fades; this stays, so the desk can see the last count landed without looking up. */}
            {lastCount && !inboundSettled && (
              <span className="last" aria-label="Last counted scan">
                Last scan <b>{lastCount.sku}</b> <span className="ok">+1</span> <time>{lastCount.at}</time>
              </span>
            )}
          </div>
        </div>
      )}

      {inbound &&
        (() => {
          const { doc, lines: inboundLines, tallies } = inbound;
          const done = inboundLines.filter((line) => line.qtyReceived === line.qtyExpected).length;
          const locationOf = (sku: string) => (inboundLocations[sku] ?? '').trim();
          const matched = inboundLines.length > 0 && done === inboundLines.length;
          const located = inboundLines.every((line) => locationOf(line.sku) !== '');
          const remaining = tallies.remaining;
          // A received or cancelled request is done with; leaving the confirms live let the desk act on it
          // and learn only from the server's refusal that there was nothing left to confirm.
          const cancelled = inboundCancelled;
          const received = inboundReceived;
          // The server states the surplus separately; nothing owed and nothing wrong are different facts.
          const overCounted =
            tallies.over ??
            inboundLines.reduce((sum, line) => sum + Math.max(line.qtyReceived - line.qtyExpected, 0), 0);
          // What still stands between this request and the confirm it runs on its own, in one line.
          const hint = cancelled
            ? `Inbound No. ${doc.inboundNo ?? doc.docId} is cancelled — read-only`
            : received
              ? `Received — ${tallies.received} in stock. Lower a Received qty to take units back out (the request opens again) · more arrived? raise the Expected qty (✎)`
              : lineQtyErrors.size > 0
                ? 'Fix the highlighted Received qty first'
                : overCounted > 0
                  ? `Over-counted by ${overCounted} — lower the Received qty, or raise the Expected qty (✎) if more arrived than requested`
                  : !matched
                    ? `${remaining} remaining — Full Inbound runs by itself once every line matches its Expected qty · fewer arrived? lower the Expected qty (✎) to what came in`
                    : !located
                      ? 'No shelf known yet for a new SKU — scan its shelf into the empty Location box; Full Inbound runs by itself once the last one is in'
                      : null;
          // The confirm the request runs on its own is due or on the wire: the card says so, and offers nothing else.
          const confirmingSelf = (autoConfirm != null && autoConfirm.docId === doc.docId) || confirming;
          return (
            <>
              {/* The request's thread: the floor's notes and the desk's own auto-comments (an Expected qty edit,
                  a linked parcel), read where the goods are received. Notes are written from the request list. */}
              <section className="cpanel" aria-label="Inbound request comments">
                <div className="fw-semibold mb-1">{`Comments (${(inbound.comments ?? []).length})`}</div>
                <InboundCommentThread comments={inbound.comments} label={`Comments on ${doc.inboundNo ?? doc.docId}`} />
              </section>
              <div className="tiles">
                <div className="tile">
                  <span>Expected Qty (Total)</span>
                  <b>{tallies.expected}</b>
                </div>
                <div className="tile ok">
                  <span>Received (scanned)</span>
                  <b>{tallies.received}</b>
                </div>
                <div className="tile warn">
                  <span>Remaining</span>
                  <b>{remaining}</b>
                </div>
                {/* Always rendered, so the desk reads the same shape whether or not anything overflowed. */}
                <div className={overCounted > 0 ? 'tile warn' : 'tile quiet'}>
                  <span>Over-count</span>
                  <b>{overCounted}</b>
                </div>
                <div className="tile sku">
                  <span>SKU</span>
                  <b>{`${inboundLines.length} SKUs (${done} done)`}</b>
                </div>
              </div>

              <div className="table-wrap">
                <table className="tbl" aria-label="Inbound request lines">
                  {/* Fixed widths: a status chip or an error line growing must not walk the columns about. */}
                  <colgroup>
                    <col className="col-sku" />
                    <col className="col-prod" />
                    <col className="col-exp" />
                    <col className="col-recv" />
                    <col className="col-loc" />
                    <col className="col-status" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>SKU No.</th>
                      <th>Brand · Product</th>
                      <th className="num">Expected Qty</th>
                      <th>Received Qty</th>
                      <th>Location</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inboundLines.map((line) => (
                      <tr key={line.lineNo} className={line.qtyReceived === line.qtyExpected ? 'row-done' : 'row-part'}>
                        <td className="num">
                          <StockHistoryLink sku={line.sku} onOpen={refocusSearch} />
                        </td>
                        <td className="prod">
                          <b>{line.brand ?? '—'}</b>
                          {line.productName ?? '—'}
                        </td>
                        <td className="num">
                          {line.qtyExpected}{' '}
                          <button
                            type="button"
                            className="qedit"
                            aria-label={`Edit ${line.sku} expected qty`}
                            disabled={busy || cancelled || inboundRefreshStale !== null}
                            onClick={() => {
                              setExpectedEdit({
                                docId: doc.docId,
                                inboundNo: doc.inboundNo ?? doc.docId,
                                lineNo: line.lineNo,
                                product: [line.brand, line.productName].filter(Boolean).join(' ') || line.sku,
                                qtyExpected: line.qtyExpected,
                                qtyReceived: line.qtyReceived ?? 0,
                                ...(line.updatedAt && { updatedAt: line.updatedAt }),
                              });
                            }}
                          >
                            ✎
                          </button>
                        </td>
                        <td>
                          <InboundReceivedQtyInput
                            key={`${doc.docId}-${line.lineNo}`}
                            id={`inbound-received-${line.sku}`}
                            sku={line.sku}
                            lineNo={line.lineNo}
                            initialQty={line.qtyReceived}
                            disabled={cancelled || inboundRefreshStale !== null || confirming}
                            onSave={(qty) => setInboundReceived(doc.docId, line.lineNo, qty)}
                            onErrorChange={handleLineQtyErrorChange}
                          />
                        </td>
                        <td>
                          {shownShelf(line) !== '' ? (
                            // The shelf is read, not typed: the request's own assignment, or wherever the sku already sits.
                            <span className="locread">{shownShelf(line)}</span>
                          ) : (
                            // A sku stocked nowhere yet has no shelf to read; this is the one box the desk types into.
                            <>
                              <label className="visually-hidden" htmlFor={`inbound-location-${line.sku}`}>
                                {line.sku} location
                              </label>
                              <CameraScanField
                                label={`Scan ${line.sku} location with camera`}
                                disabled={cancelled || confirming || inboundRefreshStale !== null}
                              >
                                <input
                                  id={`inbound-location-${line.sku}`}
                                  className="locin"
                                  placeholder="Scan the shelf"
                                  disabled={cancelled || confirming || inboundRefreshStale !== null}
                                  value={inboundLocations[line.sku] ?? ''}
                                  onBlur={askWholeCheck}
                                  {...asciiScanField(
                                    (value) => setInboundLocations((previous) => ({ ...previous, [line.sku]: value })),
                                    (event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        askWholeCheck();
                                      }
                                    },
                                  )}
                                />
                              </CameraScanField>
                            </>
                          )}
                        </td>
                        <td>
                          {line.qtyReceived === line.qtyExpected ? (
                            <span className="tag tag-done">✓ INBOUNDED</span>
                          ) : line.qtyReceived > line.qtyExpected ? (
                            <span className="tag tag-over">
                              Over-counted · {line.qtyReceived - line.qtyExpected} more than expected
                            </span>
                          ) : (
                            <span className="tag tag-part">
                              In progress · {line.qtyExpected - line.qtyReceived} remaining
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={confirmingSelf ? 'inbound-actions counting' : 'inbound-actions'}>
                {confirmingSelf ? (
                  // The confirm is already on its way; the card says so for the moment it takes.
                  <div className="autoconfirm" role="status" aria-live="assertive">
                    <b>✓ Every line matches — confirming Full Inbound…</b>
                  </div>
                ) : inboundWhole ? (
                  // Whole, but not from a hand just now (opened whole, or counted by another desk): the hand presses.
                  <button
                    type="button"
                    className="btn btn-blue"
                    disabled={busy}
                    onClick={() => void confirmFullInbound(doc.docId, inboundLines)}
                  >
                    Confirm Full Inbound
                  </button>
                ) : (
                  hint && <span className="hint">{hint}</span>
                )}
                {inboundRefreshStale && (
                  <span className="text-danger" role="alert">
                    {inboundRefreshStale === 'confirm'
                      ? 'Full Inbound was confirmed, but the latest request could not be reloaded. Refresh the page to load it again before changing anything.'
                      : 'Count was saved, but the latest request could not be reloaded. Rescan before confirming.'}
                  </span>
                )}
                <span className="mut">
                  Full Inbound runs by itself the moment the last line matches its Expected qty and has a shelf. On
                  confirm: reflected in Inventory (Current Stocks) · Inbound Request List switches to INBOUNDED ·
                  Received Date recorded automatically · a received request takes no further count
                </span>
              </div>
            </>
          );
        })()}

      {inboundDone &&
        (!inbound || ((inbound.doc.inboundNo ?? inbound.doc.docId) === inboundDone && inboundReceived)) && (
          <div className="donebanner" role="status">
            <b>✓ Full Inbound Complete — Inbound No. {inboundDone}</b>
            <div className="kv">
              <span>Inventory updated with the counted locations</span>
              <span>Inbound Request List switched to INBOUNDED</span>
              <span>Received Date recorded automatically · Carrier is not recorded</span>
            </div>
          </div>
        )}

      {banner?.kind === 'PRODUCT_BARCODE' && (
        <div className="infobanner warn" role="alert">
          <b>🏷️ {banner.productName ?? banner.sku}</b>
          <div className="mut">
            {banner.sku}
            {banner.packSize && banner.packSize > 1 ? ` · ${banner.packSize} per pack` : ''}
          </div>
          <div>{productBannerNote(banner)}</div>
          {banner.orderId != null && banner.match && (
            <ScannedLineComments
              key={banner.commentReadId}
              client={client}
              orderId={banner.orderId}
              lineNo={banner.match.lineNo}
            />
          )}
        </div>
      )}

      {banner?.kind === 'RETURN_WAYBILL' && (
        <div className="retbanner" role="alert">
          <b>⟲ Customer Return Order</b>
          <div>
            A returned tracking barcode was scanned — Order {banner.orderId} · Tracking {banner.trackingNumber}
          </div>
        </div>
      )}

      {banner?.kind === 'UNKNOWN' && (
        <div className="failbanner" role="alert">
          <b>No order matches the scanned barcode.</b>
          <div>
            {/* Reopens the lookup the scan already offered, for a desk that dismissed it and found the number after all. */}
            <button type="button" className="btn btn-line btn-sm" onClick={() => setLookupCode(banner.code ?? '')}>
              Enter order number
            </button>
            <button
              type="button"
              className="btn btn-line btn-sm"
              onClick={() =>
                setSendToPool({
                  code: banner.code ?? '',
                  trackingNo: trackingOnScreen(),
                  orderNo: null,
                  parkedScanId: banner.parkedScanId ?? null,
                })
              }
            >
              Send to unrecognized pool
            </button>
          </div>
        </div>
      )}

      {/* Where the dead end sat, in red: a refused send is a failure the desk has to answer, not a notice. */}
      {poolRefusal && (
        <div className="failbanner" role="alert" aria-label="Not sent to the unrecognized pool">
          <b>Not sent to the unrecognized pool — {poolRefusal.reason}</b>
          <div className="guidance">
            <b>What to do with this parcel:</b> {poolRefusal.guidance}
          </div>
          <div className="choices">
            {poolRefusal.parkable && (
              <button
                type="button"
                className="btn btn-blue btn-sm"
                disabled={busy}
                onClick={() => void registerUnrecognized({ ...poolRefusal.request, force: true })}
              >
                Park it anyway
              </button>
            )}
            <button type="button" className="btn btn-gray btn-sm" onClick={() => setPoolRefusal(null)}>
              {poolRefusal.parkable ? 'Check the scan again' : 'Dismiss'}
            </button>
          </div>
        </div>
      )}

      {violations.length > 0 && (
        <div className="failbanner" role="alert">
          <b>Action blocked</b>
          <ul>
            {violations.map((violation) => (
              <li key={violation}>{violation}</li>
            ))}
          </ul>
        </div>
      )}

      {(failures.lines.length > 0 || skippedLines.length > 0) && (
        <div className="infobanner warn" role="alert">
          <b>Some lines were not {failures.work}</b>
          <ul>
            {/* Passed over and refused ask different things of the desk, so they are not one list [D-q]. */}
            {skippedLines.map((line) => (
              <li key={`skipped-${line.lineNo}`}>
                Line {line.lineNo} — passed over: {line.reason}
              </li>
            ))}
            {failures.lines.map((failure) => (
              <li key={`failed-${failure.lineNo}`}>
                Line {failure.lineNo} — {failure.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {order && header && liveCard && (
        <>
          <p className="sub ff-section" style={{ marginTop: 18 }}>
            Search Results
          </p>
          {renderOrderCard(liveCard)}
        </>
      )}

      <LiveBarcodeFeed scans={feed.scans} />

      {confirmDialog}

      {assignAsk && (
        <AssignBarcodeDialog
          code={assignAsk.code}
          options={assignAsk.options}
          initial={assignAsk.initial}
          onPick={(lineNo) => {
            assignAsk.settle(lineNo);
            setAssignAsk(null);
          }}
          onCancel={() => {
            assignAsk.settle(null);
            setAssignAsk(null);
          }}
          onScanned={(scanned) => {
            assignAsk.settle({ scanned });
            setAssignAsk(null);
          }}
          onNotProduct={() => {
            assignAsk.settle('not-product');
            setAssignAsk(null);
          }}
        />
      )}

      {cancelTarget && (
        <CancelInboundModal
          target={cancelTarget}
          busy={busy}
          onClose={() => setCancelTarget(null)}
          onConfirm={(request) => void confirmCancelInbound(request)}
        />
      )}

      {returnLabelOpen && (
        <ReturnLabelModal
          lines={selectedLines}
          onPrint={(lineNos, label) => void printReturnLabels(lineNos, label)}
          onClose={() => setReturnLabelOpen(false)}
        />
      )}
      {expectedEdit !== null && (
        <EditExpectedQtyModal
          target={expectedEdit}
          busy={busy}
          onClose={() => setExpectedEdit(null)}
          onConfirm={(qty, reason, memo) => void saveExpectedQty(expectedEdit, qty, reason, memo)}
        />
      )}
      {lookupCode !== null && (
        <PurchaseOrderLookupModal
          code={lookupCode}
          busy={busy}
          onLookup={lookupPurchaseOrder}
          onMatch={(candidate) => void matchScannedTracking(lookupCode, candidate)}
          onSendToPool={(orderNo) => {
            setLookupCode(null);
            setSendToPool({
              code: lookupCode,
              trackingNo: trackingOnScreen(),
              orderNo,
              parkedScanId: banner?.parkedScanId ?? null,
            });
          }}
          onClose={() => setLookupCode(null)}
        />
      )}
      {sendToPool && (
        <SendToUnrecognizedModal
          target={sendToPool}
          busy={busy}
          onClose={() => setSendToPool(null)}
          onConfirm={(request) => void registerUnrecognized(request)}
        />
      )}

      {returnRestockOpen && (
        <ReturnRestockModal
          lines={selectedLines}
          knownLocations={locations}
          busy={busy}
          onClose={() => setReturnRestockOpen(false)}
          onConfirm={(restockLines, memo) => void confirmReturnRestock(restockLines, memo)}
        />
      )}

      {settingsOpen && (
        <ScanDeskSettingsModal
          autoPrintOnScan={autoPrintOnScan}
          onAutoPrintOnScanChange={(on) => {
            setAutoPrintOnScan(on);
            saveAutoPrintOnScan(on);
          }}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
}
